import React, {useEffect, useRef, useState} from 'react';
import '@google/model-viewer';
import L from 'leaflet';
import {KioskBottomBar} from './KioskBottomBar';
import {
  OFFLINE_CACHE_NAME,
  readOfflineSnapshot,
  writeOfflineSnapshot,
} from './offlineCache';

declare const __FINDIFY_WEB_CONFIG__: Record<string, string> | undefined;
const config =
  typeof __FINDIFY_WEB_CONFIG__ === 'undefined' ? {} : __FINDIFY_WEB_CONFIG__;
const API_URL = config.API_URL || 'https://api.findify.cloud';
const WORKSPACE_ID = config.WORKSPACE_ID || '203';
const DOMAIN_ID = config.DOMAIN_ID || '';

type Media =
  | string
  | {
      file_path?: string;
      file_url_path?: string;
      url?: string;
      file_url?: string;
      url_path?: string;
    };

const localized = (value: any, language = 'pt-PT'): any => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const languageAliases =
    language === 'en-US' ? ['en-US', 'en-GB', 'en'] : [language];
  for (const key of languageAliases) {
    const translation = value[key];
    if (typeof translation === 'string' && translation.trim()) return translation;
    if (translation !== undefined && translation !== null && translation !== '') return translation;
  }
  return (
    value.pt ?? value.default ?? Object.values(value).find(item => item !== '')
  );
};

const mediaUrl = (value: any): string | undefined => {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const url = mediaUrl(entry);
      if (url) return url;
    }
    return undefined;
  }
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && /^\d+$/.test(value))
  ) {
    return `${API_URL}/uploads/${value}`;
  }
  const media =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('file_path' in value ||
      'file_url_path' in value ||
      'file_url' in value ||
      'url_path' in value ||
      'url' in value)
      ? value
      : localized(value);
  if (typeof media === 'string') {
    if (media.startsWith('http')) return media;
    return `${API_URL}/uploads/${encodeURI(media.replace(/^\//, ''))}`;
  }
  if (!media || typeof media !== 'object') return undefined;
  const item = media as Media;
  return mediaUrl(
    item.file_url_path ??
      item.file_path ??
      item.url ??
      item.file_url ??
      item.url_path,
  );
};

const contentImage = (content: any) =>
  mediaUrl(
    content?.file_image_media_by_language ??
      content?.file_image_media_localized ??
      content?.file_image_media ??
      content?.image_media_by_language ??
      content?.image_media ??
      content?.file_image ??
      content?.image ??
      content?.featured_image_media ??
      content?.featured_image_media_by_language ??
      content?.featured_image,
  );
const contentImages = (content: any, language = 'pt-PT') => {
  const featured = contentImage(content);
  const gallery = localized(
    content?.galleries ??
      content?.gallery ??
      content?.gallery_images ??
      content?.gallery_media ??
      content?.image_gallery ??
      content?.images,
    language,
  );
  const galleryEntries = Array.isArray(gallery)
    ? gallery
    : gallery && typeof gallery === 'object'
      ? gallery.images ?? gallery.items ?? gallery.files ?? gallery.media ?? [gallery]
      : [];
  const images = [...galleryEntries]
    .sort((a: any, b: any) => Number(a?.sequence ?? 0) - Number(b?.sequence ?? 0))
    .map((entry: any) =>
      mediaUrl(
        entry?.image_media ??
          entry?.file_image_media ??
          entry?.featured_image_media ??
          entry?.image ??
          entry?.file_image ??
          entry?.media ??
          entry,
      ),
    )
    .filter(Boolean);
  return Array.from(new Set([featured, ...images].filter(Boolean))) as string[];
};
const content3d = (content: any) =>
  mediaUrl(
    content?.file_3d_media_by_language ??
      content?.file_3d_media ??
      content?.model_3d ??
      content?.three_d_media ??
      content?.media_3d ??
      content?.model3d ??
      content?.file_3d,
  );

type OfflineResource = {url: string; kind: 'api' | 'image' | 'video' | 'audio' | 'model3d' | 'map'; expectedBytes?: number};

const OFFLINE_MEDIA_FIELDS: Record<OfflineResource['kind'], string[]> = {
  api: [],
  image: [
    'featured_image', 'featured_image_media', 'featured_image_media_by_language',
    'file_image', 'file_image_media', 'file_image_media_by_language',
    'file_image_media_localized', 'image', 'image_media', 'image_media_by_language',
    'galleries', 'gallery', 'gallery_images', 'gallery_media', 'gallery_image',
    'image_gallery', 'images', 'files', 'media',
  ],
  video: ['video', 'video_media', 'file_video', 'file_video_media', 'file_video_media_by_language'],
  audio: ['audio', 'audio_media', 'file_audio', 'file_audio_media', 'file_audio_media_by_language'],
  model3d: ['file_3d', 'file_3d_media', 'file_3d_media_by_language', 'model_3d', 'model3d', 'three_d_media', 'media_3d'],
  map: [],
};

const OFFLINE_MEDIA_CHILD_FIELDS = new Set([
  ...OFFLINE_MEDIA_FIELDS.image,
  ...OFFLINE_MEDIA_FIELDS.video,
  ...OFFLINE_MEDIA_FIELDS.audio,
  ...OFFLINE_MEDIA_FIELDS.model3d,
  'image_media', 'file_image_media', 'featured_image_media', 'image', 'file_image',
  'video_media', 'file_video_media', 'audio_media', 'file_audio_media',
  'file', 'src', 'url', 'file_url', 'file_url_path', 'file_path', 'url_path',
]);

const isLocaleKey = (key: string) => /^[a-z]{2}(?:-[A-Z]{2})?$/.test(key);

const collectMediaResources = (
  value: any,
  kind: OfflineResource['kind'],
  field: string,
): OfflineResource[] => {
  if (Array.isArray(value))
    return value.flatMap(entry => collectMediaResources(entry, kind, field));
  if (typeof value === 'string' || typeof value === 'number') {
    // Public API objects expose numeric media-manager IDs beside the resolved
    // `*_media` object. They are not `/uploads/<id>` URLs; only cache actual
    // paths/URLs here and leave the numeric IDs to their metadata siblings.
    const isBareMediaId = typeof value === 'number' || /^\d+$/.test(value);
    const url = OFFLINE_MEDIA_CHILD_FIELDS.has(field) && !isBareMediaId
      ? mediaUrl(value)
      : undefined;
    return url ? [{url, kind}] : [];
  }
  if (!value || typeof value !== 'object') return [];

  if ('file_path' in value || 'file_url_path' in value || 'file_url' in value || 'url_path' in value || 'url' in value) {
    const url = mediaUrl(value);
    return url ? [{url, kind, expectedBytes: Number(value.file_size) || undefined}] : [];
  }

  return Object.entries(value).flatMap(([key, child]) =>
    isLocaleKey(key) || OFFLINE_MEDIA_CHILD_FIELDS.has(key)
      ? collectMediaResources(child, kind, key)
      : [],
  );
};

const offlineResourcesFor = (items: any[]): OfflineResource[] => {
  const resources = new Map<string, OfflineResource>();
  const add = (resource: OfflineResource) => {
    const existing = resources.get(resource.url);
    resources.set(resource.url, {
      ...existing,
      ...resource,
      expectedBytes: resource.expectedBytes ?? existing?.expectedBytes,
      kind: resource.kind === 'model3d' || existing?.kind === 'model3d'
        ? 'model3d'
        : resource.kind,
    });
  };

  items.forEach(item => {
    (Object.keys(OFFLINE_MEDIA_FIELDS) as OfflineResource['kind'][]).forEach(kind => {
      OFFLINE_MEDIA_FIELDS[kind].forEach(field => {
        collectMediaResources(item?.[field], kind, field).forEach(add);
      });
    });
    ['description', 'page_description', 'resume'].forEach(field => {
      const collectTextUrls = (value: any) => {
        if (Array.isArray(value)) return value.forEach(collectTextUrls);
        if (value && typeof value === 'object') return Object.values(value).forEach(collectTextUrls);
        if (typeof value !== 'string') return;
        const matches = value.matchAll(/(?:src|poster)=["'](https?:\/\/[^"']+|\/uploads\/[^"']+)["']/gi);
        for (const match of matches) add({url: new URL(match[1], API_URL).href, kind: 'image'});
      };
      collectTextUrls(item?.[field]);
    });
  });
  return Array.from(resources.values());
};

const cacheOfflineResources = async (
  resources: OfflineResource[],
  onProgress: (completed: number, total: number) => void,
) => {
  const cache = await caches.open(OFFLINE_CACHE_NAME);
  const failed: string[] = [];
  let nextIndex = 0;
  let completed = 0;
  const cacheOne = async (resource: OfflineResource) => {
    try {
      let response: Response;
      try {
        response = await fetch(resource.url, {
          cache: 'reload',
          credentials: 'omit',
          mode: 'cors',
        });
      } catch (error) {
        if (['api', 'model3d'].includes(resource.kind)) throw error;
        response = await fetch(resource.url, {
          cache: 'reload',
          credentials: 'omit',
          mode: 'no-cors',
        });
      }

      if (response.type !== 'opaque' && !response.ok)
        throw new Error(`HTTP ${response.status}`);
      if (response.type === 'opaque' && ['api', 'model3d'].includes(resource.kind))
        throw new Error('Resposta opaca, não verificável para este ficheiro');

      const responseBytes = Number(response.headers.get('content-length'));
      if (
        resource.kind === 'model3d' &&
        resource.expectedBytes &&
        responseBytes &&
        responseBytes !== resource.expectedBytes
      ) {
        throw new Error(`Tamanho recebido ${responseBytes}, esperado ${resource.expectedBytes}`);
      }

      await cache.put(resource.url, response.clone());
      const saved = await cache.match(resource.url);
      if (!saved) throw new Error('O recurso não foi encontrado após a gravação');
      if (resource.kind === 'model3d' && saved.type === 'opaque')
        throw new Error('O modelo 3D ficou guardado sem leitura verificável');
    } catch (error) {
      const label = resource.url.split('/').pop()?.split('?')[0] || resource.url;
      failed.push(`${label}: ${error instanceof Error ? error.message : 'falha no descarregamento'}`);
    }
  };

  const worker = async () => {
    while (nextIndex < resources.length) {
      const index = nextIndex++;
      await cacheOne(resources[index]);
      completed++;
      onProgress(completed, resources.length);
    }
  };
  await Promise.all(Array.from({length: Math.min(2, resources.length)}, worker));
  return {failed, completed, total: resources.length};
};

const fetchAllApiResults = async (baseUrl: string) => {
  const results: any[] = [];
  const urls: string[] = [];
  const limit = 100;
  let offset = 0;
  let expectedTotal = Number.POSITIVE_INFINITY;
  do {
    const requestUrl = new URL(baseUrl);
    requestUrl.searchParams.set('offset', String(offset));
    requestUrl.searchParams.set('limit', String(limit));
    urls.push(requestUrl.href);
    const response = await fetch(requestUrl.href);
    if (!response.ok) throw new Error(`${response.status} em ${requestUrl.pathname}`);
    const payload = await response.json();
    const page = payload?.results ?? payload?.result ?? payload?.data ?? [];
    if (!Array.isArray(page)) throw new Error(`Formato de resposta inválido em ${requestUrl.pathname}`);
    results.push(...page);
    const rawTotal = payload?.count ?? payload?.total ?? payload?.total_count;
    const total = rawTotal === null || rawTotal === undefined || rawTotal === ''
      ? Number.NaN
      : Number(rawTotal);
    if (Number.isFinite(total) && total >= 0) expectedTotal = total;
    if (results.length >= expectedTotal || page.length < limit) break;
    offset += limit;
    if (offset >= 100000) throw new Error(`Paginação excedeu o limite em ${requestUrl.pathname}`);
  } while (true);
  return {results, urls};
};

const contentText = (content: any, language = 'pt-PT') => {
  const value = localized(
    content?.summary ??
      content?.resume ??
      content?.description ??
      content?.excerpt,
    language,
  );
  return typeof value === 'string' ? value : '';
};

const glossaryLocalizedValue = (value: any, language = 'pt-PT') => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value ?? '';
  const keys = language === 'en-US' ? ['en-US', 'en-GB', 'en'] : [language];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(value, key)) return value[key] ?? '';
  }
  return '';
};

const glossaryTitle = (content: any, language = 'pt-PT') => {
  for (const field of [content?.title, content?.name]) {
    const value = glossaryLocalizedValue(field, language);
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const glossaryText = (content: any, language = 'pt-PT') => {
  for (const field of [content?.summary, content?.resume, content?.description, content?.excerpt]) {
    const value = glossaryLocalizedValue(field, language);
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const contentHtml = (content: any, language = 'pt-PT') =>
  localized(content?.description ?? content?.page_description ?? '', language);
const plainText = (value: any, language = 'pt-PT') =>
  String(titleOf(value, language) || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const firstParagraph = (content: any, language = 'pt-PT') => {
  const source = String(
    titleOf(
      content?.description ?? content?.page_description ?? '',
      language,
    ) || '',
  ).trim();
  const paragraph = source.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i)?.[1];
  if (paragraph) return plainText(paragraph, language);
  return plainText(source.split(/\n\s*\n/)[0] || source, language);
};
const titleOf = (value: any, language = 'pt-PT') =>
  localized(value, language) || '';

let dynamicManifestUrl: string | undefined;

const updateBrowserIdentity = (title: string, logo?: string) => {
  document.title = title;
  if (!logo) return;
  let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.appendChild(favicon);
  }
  favicon.href = logo;
  const appleIcon = document.querySelector<HTMLLinkElement>(
    'link[rel="apple-touch-icon"]',
  );
  if (appleIcon) appleIcon.href = logo;

  // The static manifest is a generic fallback. Once workspace settings have
  // loaded, publish the configured app logo as the PWA install icon too.
  const manifestLink = document.querySelector<HTMLLinkElement>(
    'link[rel="manifest"]',
  );
  if (manifestLink) {
    try {
      const logoUrl = new URL(logo, window.location.href);
      const extension = logoUrl.pathname.split('.').pop()?.toLowerCase();
      const mimeType =
        extension === 'svg'
          ? 'image/svg+xml'
          : extension === 'jpg' || extension === 'jpeg'
            ? 'image/jpeg'
            : extension === 'webp'
              ? 'image/webp'
              : 'image/png';
      const workspaceManifest = {
        name: 'Aplicação do Museu',
        short_name: 'Museu',
        description: 'Aplicação interativa em modo kiosk.',
        id: '/',
        start_url: '/',
        scope: '/',
        display: 'standalone',
        orientation: 'landscape',
        background_color: '#000000',
        theme_color: '#000000',
        icons: [
          {src: logoUrl.href, sizes: '192x192', type: mimeType, purpose: 'any'},
          {src: logoUrl.href, sizes: '512x512', type: mimeType, purpose: 'any'},
        ],
      };
      const manifestBlob = new Blob([JSON.stringify(workspaceManifest)], {
        type: 'application/manifest+json',
      });
      const nextManifestUrl = URL.createObjectURL(manifestBlob);
      const previousManifestUrl = dynamicManifestUrl;
      dynamicManifestUrl = nextManifestUrl;
      manifestLink.href = nextManifestUrl;
      if (previousManifestUrl)
        window.setTimeout(() => URL.revokeObjectURL(previousManifestUrl), 60_000);
    } catch {
      // Keep the static manifest if this browser cannot create a dynamic one.
    }
  }
};

const hexToRgba = (value: string, alpha: number) => {
  const hex = String(value || '').replace('#', '');
  const normalized =
    hex.length === 3
      ? hex
          .split('')
          .map(part => part + part)
          .join('')
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return `rgba(51, 20, 16, ${alpha})`;
  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const colorValue = (value: any, fallback: string) => {
  const hex = String(value ?? '')
    .trim()
    .replace('#', '');
  return /^[0-9a-f]{8}$/i.test(hex)
    ? `#${hex.slice(0, 6)}`
    : /^[0-9a-f]{6}$/i.test(hex) || /^[0-9a-f]{3}$/i.test(hex)
      ? `#${hex}`
      : fallback;
};

const updateTheme = (colors: any = {}) => {
  const root = document.documentElement;
  const bottomBarColor = colorValue(
    colors.bottom_bar || colors.bottom_bar_color,
    '#331410',
  );
  root.style.setProperty(
    '--app-main-color',
    colorValue(colors.main_color, '#ef654f'),
  );
  root.style.setProperty(
    '--app-background-color',
    colorValue(colors.background_color, '#f5efeb'),
  );
  root.style.setProperty(
    '--app-text-color',
    colorValue(colors.text_color, '#3e3836'),
  );
  root.style.setProperty(
    '--app-secondary-color',
    colorValue(colors.secondary_color, '#8f5a0a'),
  );
  root.style.setProperty('--app-white-color', '#ffffff');
  root.style.setProperty('--app-bottom-bar-color', bottomBarColor);
  root.style.setProperty(
    '--app-bottom-bar-overlay',
    hexToRgba(bottomBarColor, 0.8),
  );
};

const findContent = (payload: any, slug: string) => {
  const items = payload?.results ?? payload?.result ?? payload ?? [];
  return (Array.isArray(items) ? items : [items]).find(
    (item: any) =>
      String(item?.slug ?? item?.page_slug ?? '').toLowerCase() === slug,
  );
};

const categoryIdsOf = (item: any) =>
  (item?.categories ?? [])
    .map((line: any) =>
      Number(line?.category_id ?? line?.category?.category_id ?? line),
    )
    .filter(Number.isFinite);

const descendantCategoryIds = (rootId: any, categories: any[]) => {
  const ids = new Set([Number(rootId)]);
  let changed = true;
  while (changed) {
    changed = false;
    categories.forEach((category: any) => {
      const parentId = Number(
        category?.parent_id ??
          category?.parent_category_id ??
          category?.parent?.category_id,
      );
      if (ids.has(parentId) && !ids.has(Number(category.category_id))) {
        ids.add(Number(category.category_id));
        changed = true;
      }
    });
  }
  return ids;
};

const quizSlugFromMenu = (menu: any) =>
  String(menu?.url ?? '')
    .replace(/^\/+/, '')
    .match(/^quizzes\/([^/?#]+)/i)?.[1] ?? '';

const quizMedia = (value: any) => {
  const raw =
    value?.featured_image_media ??
    value?.featured_image_media_by_language ??
    value?.featured_image_localized ??
    value?.featured_image ??
    value?.image_media ??
    value?.image ??
    value?.media ??
    value?.video ??
    value?.audio ??
    value;
  if (!raw || typeof raw === 'boolean') return '';
  if (typeof raw === 'object')
    return mediaUrl(
      raw.file_url ??
        raw.file_url_path ??
        raw.url ??
        raw.path ??
        raw.src ??
        raw.media_url ??
        raw.id ??
        '',
    );
  return mediaUrl(raw);
};

const normalizeQuiz = (raw: any, language = 'pt-PT') => {
  const source = raw?.quiz ?? raw?.data ?? raw?.result ?? raw;
  const questionsRaw =
    source?.questions ?? source?.quiz_questions ?? source?.items ?? [];
  const questions = (Array.isArray(questionsRaw) ? questionsRaw : []).map(
    (question: any, index: number) => {
      const optionsRaw =
        question.options ?? question.answers ?? question.choices ?? [];
      const options = (Array.isArray(optionsRaw) ? optionsRaw : [])
        .sort(
          (a: any, b: any) => Number(a.position ?? 0) - Number(b.position ?? 0),
        )
        .map((option: any, optionIndex: number) => ({
          id: String(
            option.answer_id ?? option.id ?? option.option_id ?? optionIndex,
          ),
          label: titleOf(
            option.label ?? option.title ?? option.text ?? option.name,
            language,
          ),
          image: quizMedia(option),
          correct: Boolean(
            option.is_correct ?? option.correct ?? option.isCorrect,
          ),
          correctFeedback: titleOf(
            option.correct_feedback ??
              option.correctFeedback ??
              option.feedback_correct ??
              option.feedback?.correct,
            language,
          ),
          incorrectFeedback: titleOf(
            option.incorrect_feedback ??
              option.incorrectFeedback ??
              option.feedback_incorrect ??
              option.feedback?.incorrect,
            language,
          ),
          explanation: titleOf(
            option.explanation ??
              option.answer_explanation ??
              option.feedback_explanation,
            language,
          ),
          points: Number(option.points ?? option.score ?? 0),
        }));
      const correctValue =
        question.correct_option_id ??
        question.correct_answer_id ??
        question.correct_answer ??
        question.correctAnswer;
      if (correctValue !== undefined)
        options.forEach((option: any) => {
          option.correct =
            option.correct || String(option.id) === String(correctValue);
        });
      return {
        id: String(question.id ?? question.question_id ?? index),
        prompt: titleOf(
          question.question ??
            question.prompt ??
            question.question_text ??
            question.text ??
            question.title ??
            question.name,
          language,
        ),
        image: quizMedia(question),
        video: quizMedia(question.video),
        audio: quizMedia(question.audio),
        type: question.type ?? 'single_choice',
        options,
        explanation: titleOf(
          question.explanation ??
            question.feedback ??
            question.answer_explanation,
          language,
        ),
        timeLimit: Number(
          question.time_limit ??
            source.settings?.time_per_question ??
            source.settings?.time_limit ??
            source.time_limit ??
            25,
        ),
        points: Number(question.score ?? question.points ?? 1),
        category:
          question.category ?? question.category_id ?? question.category_name,
      };
    },
  );
  return {
    ...source,
    title: titleOf(source.title ?? source.name, language),
    introTitle: titleOf(
      source.intro_title ?? source.introTitle ?? source.start_title,
      language,
    ),
    description: titleOf(
      source.description ?? source.summary ?? source.introduction,
      language,
    ),
    initialMessage: titleOf(
      source.initial_message ?? source.intro_message ?? source.resume,
      language,
    ),
    finalMessage: titleOf(source.final_message, language),
    image: quizMedia(
      source.featured_image_media ??
        source.featured_image_media_by_language ??
        source.featured_image_localized ??
        source.featured_image ??
        source,
    ),
    finalImage: quizMedia(
      source.final_image_media ??
        source.final_image_media_by_language ??
        source.final_image_localized ??
        source.final_image,
    ),
    questions,
    timeLimit: Number(
      source.settings?.time_per_question ??
        source.settings?.time_limit ??
        source.time_limit ??
        25,
    ),
  };
};

const fetchQuiz = async (domain: string, slug: string, language = 'pt-PT') => {
  const endpoints = [
    `${API_URL}/quizzes/public/${encodeURIComponent(slug)}?locale=${encodeURIComponent(language)}`,
    `${API_URL}/quizzes/public?locale=${encodeURIComponent(language)}&slug=${encodeURIComponent(slug)}`,
    `${API_URL}/front/${domain}/quizzes/${encodeURIComponent(slug)}`,
    `${API_URL}/front/${domain}/quizzes?slug=${encodeURIComponent(slug)}`,
    `${API_URL}/front/${domain}/quizzes?slug_eq=${encodeURIComponent(slug)}`,
    `${API_URL}/front/${domain}/quiz/${encodeURIComponent(slug)}`,
    `${API_URL}/front/${domain}/quiz?slug=${encodeURIComponent(slug)}`,
    `${API_URL}/mobile/quizzes?slug=${encodeURIComponent(slug)}&workspace_id_eq=${WORKSPACE_ID}`,
  ];
  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint);
      if (!response.ok) continue;
      const payload = await response.json();
      const candidate =
        payload?.results?.[0] ??
        payload?.result?.[0] ??
        payload?.data?.[0] ??
        payload?.quiz ??
        payload;
      const normalized = normalizeQuiz(candidate, language);
      if (normalized.questions.length) return normalized;
    } catch {
      /* try the next public API shape */
    }
  }
  return undefined;
};

const warmOfflineCache = async (
  domain: string,
  initialContents: any[],
  menu: any[],
  categories: any[],
  glossary: any[],
  glossaryCategoryId?: number,
) => {
  const errors: string[] = [];
  const report = (percent: number, label: string) =>
    window.dispatchEvent(
      new CustomEvent('findify-cache-progress', {
        detail: {percent, label},
      }),
    );
  try {
    report(3, 'A preparar os dados e o armazenamento');
    let persistentStorage = false;
    if (navigator.storage?.persist)
      persistentStorage = await navigator.storage.persist().catch(() => false);

    const menuCategoryIds = Array.from(
      new Set(menu.map(item => item.content_category).filter(Boolean)),
    );
    const glossaryUrl = glossaryCategoryId
      ? `${API_URL}/front/${domain}/contents?categories.category.category_id=${encodeURIComponent(glossaryCategoryId)}`
      : undefined;
    const listUrls = [
      `${API_URL}/front/${domain}/contents`,
      `${API_URL}/front/${domain}/categories`,
      `${API_URL}/front/${domain}/points_of_interest`,
      `${API_URL}/front/${domain}/routes`,
      ...menuCategoryIds.map(
        categoryId =>
          `${API_URL}/front/${domain}/contents?categories.category.category_id=${encodeURIComponent(categoryId)}`,
      ),
      ...(glossaryUrl ? [glossaryUrl] : []),
    ];
    const listResults = await Promise.all(
      listUrls.map(async url => {
        try {
          return await fetchAllApiResults(url);
        } catch (error) {
          errors.push(`${new URL(url).pathname}: ${error instanceof Error ? error.message : 'falha ao descarregar a lista'}`);
          return {results: [], urls: [url]};
        }
      }),
    );
    report(20, 'Listas e conteúdos paginados descarregados');

    const [contentData, categoryData, poiData, routeData] = listResults;
    const menuContentData = listResults.slice(4, 4 + menuCategoryIds.length);
    const glossaryData = glossaryUrl ? listResults[listResults.length - 1] : undefined;
    const routeSummaries = routeData?.results ?? [];
    const poiSummaries = poiData?.results ?? [];
    const routeDetailUrls = routeSummaries.map(
      (route: any) =>
        `${API_URL}/front/${domain}/routes/${encodeURIComponent(route.route_id ?? route.page_slug)}`,
    );
    const poiDetailUrls = poiSummaries.map(
      (poi: any) =>
        `${API_URL}/front/${domain}/points_of_interest/${encodeURIComponent(poi.point_of_interest_id ?? poi.poi_id ?? poi.id)}`,
    );
    const detailUrls = [...routeDetailUrls, ...poiDetailUrls];
    const detailResults = await Promise.all(
      detailUrls.map(async (url, index) => {
        const fallback = index < routeSummaries.length
          ? routeSummaries[index]
          : poiSummaries[index - routeSummaries.length];
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return await response.json();
        } catch (error) {
          errors.push(`${url.split('/').pop()}: ${error instanceof Error ? error.message : 'falha no detalhe'}`);
          return fallback;
        }
      }),
    );
    const routeDetails = detailResults.slice(0, routeSummaries.length);
    const poiDetails = detailResults.slice(routeSummaries.length);
    report(35, 'Detalhes de todos os pontos e roteiros descarregados');

    const allItems = [
      ...initialContents,
      ...contentData.results,
      ...categoryData.results,
      ...poiSummaries,
      ...routeSummaries,
      ...menuContentData.flatMap(result => result.results),
      ...(glossaryData?.results ?? glossary),
      ...detailResults,
    ];
    let tileResources: OfflineResource[] = [];
    const routeCoordinates = routeDetails
      .flatMap((route: any) => route?.points_of_interest ?? [])
      .map((entry: any) => entry.point_of_interest ?? entry)
      .map((point: any) => {
        const match = String(point?.lat_long ?? '').match(
          /POINT\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i,
        );
        return match ? [Number(match[1]), Number(match[2])] : null;
      })
      .filter(Boolean) as [number, number][];
    if (routeCoordinates.length) {
      const latitudes = routeCoordinates.map(point => point[0]);
      const longitudes = routeCoordinates.map(point => point[1]);
      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);
      const minLon = Math.min(...longitudes);
      const maxLon = Math.max(...longitudes);
      const tileUrls: string[] = [];
      for (const zoom of [12, 13, 14, 15, 16, 17]) {
        const tileXAtZoom = (longitude: number) =>
          Math.floor(((longitude + 180) / 360) * 2 ** zoom);
        const tileYAtZoom = (latitude: number) =>
          Math.floor(
            ((1 - Math.asinh(Math.tan((latitude * Math.PI) / 180)) / Math.PI) /
              2) *
              2 ** zoom,
          );
        const minX = Math.max(0, tileXAtZoom(minLon) - 1);
        const maxX = tileXAtZoom(maxLon) + 1;
        const minY = Math.max(0, tileYAtZoom(maxLat) - 1);
        const maxY = tileYAtZoom(minLat) + 1;
        for (let x = minX; x <= maxX && tileUrls.length < 500; x++) {
          for (let y = minY; y <= maxY && tileUrls.length < 500; y++) {
            tileUrls.push(
              `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
            );
          }
        }
      }
      tileResources = tileUrls.map(url => ({url, kind: 'map' as const}));
    }
    report(48, 'A identificar imagens, galerias, áudio, vídeo e modelos 3D');
    const mediaResources = offlineResourcesFor(allItems);
    const apiUrls = Array.from(
      new Set([
        ...listResults.flatMap(result => result.urls),
        ...detailUrls,
        `${API_URL}/mobile/workspaces?offset=0&limit=100&workspace_id_eq=${WORKSPACE_ID}`,
      ]),
    );
    const resourcesByUrl = new Map<string, OfflineResource>();
    [...apiUrls.map(url => ({url, kind: 'api' as const})), ...mediaResources, ...tileResources].forEach(resource => {
      const existing = resourcesByUrl.get(resource.url);
      resourcesByUrl.set(resource.url, {
        ...existing,
        ...resource,
        expectedBytes: resource.expectedBytes ?? existing?.expectedBytes,
        kind: resource.kind === 'model3d' || existing?.kind === 'model3d' ? 'model3d' : resource.kind,
      });
    });
    const resources = Array.from(resourcesByUrl.values());
    report(52, `A descarregar e verificar ${resources.length} recursos`);
    let mediaCacheResult = {failed: [] as string[], completed: 0, total: resources.length};
    try {
      mediaCacheResult = await cacheOfflineResources(resources, (completed, total) => {
        const percent = 52 + Math.round((completed / Math.max(1, total)) * 43);
        report(percent, `A guardar recursos offline: ${completed} de ${total}`);
      });
      errors.push(...mediaCacheResult.failed);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Não foi possível abrir o cache offline');
    }
    const serviceWorkerRegistrations = await navigator.serviceWorker?.getRegistrations().catch(() => []) ?? [];
    const serviceWorkerActive = serviceWorkerRegistrations.some(
      registration => Boolean(registration.active?.scriptURL.endsWith('/service-worker.js')),
    );
    if (!serviceWorkerActive)
      errors.push('Service Worker não está ativo; o arranque offline da aplicação não está garantido neste ambiente.');

    const complete = errors.length === 0 && mediaCacheResult.completed === mediaCacheResult.total;
    report(complete ? 97 : 96, complete
      ? 'Recursos descarregados; a gravar o snapshot offline'
      : `Cache incompleta: ${errors.length} falhas por resolver`);
    return {
      routes: routeDetails,
      pointsOfInterest: poiDetails,
      contentsByCategory: Object.fromEntries(
        menuCategoryIds.map((categoryId, index) => [
          String(categoryId),
          menuContentData[index]?.results ?? [],
        ]),
      ),
      contents: contentData.results,
      categories: categoryData.results.length ? categoryData.results : categories,
      glossary: glossaryData?.results ?? glossary,
      complete,
      persistentStorage,
      errors,
      resourceCount: resources.length,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro inesperado ao preparar o cache offline.';
    errors.push(message);
    report(96, `Cache incompleta: ${message}`);
    return {
      routes: [], pointsOfInterest: [], contentsByCategory: {},
      contents: initialContents, categories, glossary, complete: false,
      persistentStorage: false, errors, resourceCount: 0,
    };
  }
};

const fetchJsonWithOfflineCache = async (url: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (networkError) {
    try {
      const cached = await caches.match(url);
      if (cached?.ok) return await cached.json();
    } catch {
      /* use the caller's local snapshot fallback */
    }
    throw networkError;
  }
};

const getNetworkData = async () => {
  const workspaceResponse = await fetch(
    `${API_URL}/mobile/workspaces?offset=0&limit=100&workspace_id_eq=${WORKSPACE_ID}`,
  );
  if (!workspaceResponse.ok)
    throw new Error(
      `Não foi possível carregar o workspace (${workspaceResponse.status}).`,
    );
  const workspacePayload = await workspaceResponse.json();
  const workspace =
    (workspacePayload?.results ?? []).find(
      (item: any) => String(item.workspace_id) === WORKSPACE_ID,
    ) ?? workspacePayload?.result?.[0];
  const domain = DOMAIN_ID || workspace?.primary_domain?.domain_name;
  if (!domain)
    throw new Error('O workspace não tem domínio público configurado.');
  updateBrowserIdentity(
    localized(workspace?.app_settings?.general?.title) ||
      localized(workspace?.name) ||
      'Findify',
    workspace?.app_settings?.app_logo_url,
  );

  const contentsResponse = await fetch(
    `${API_URL}/front/${domain}/contents?offset=0&limit=100`,
  );
  if (!contentsResponse.ok)
    throw new Error(
      `Não foi possível carregar os conteúdos (${contentsResponse.status}).`,
    );
  const contents = await contentsResponse.json();
  const screensaver = findContent(contents, 'screensaver');
  const touchhand = findContent(contents, 'touchhand');
  const screensaverWaitTime = findContent(contents, 'screensaverwaittime');
  const partnerLogos = findContent(contents, 'logosparceiros');
  const texts = findContent(contents, 'textos');
  const photography = findContent(contents, 'fotography');
  const categoriesResponse = await fetch(
    `${API_URL}/front/${domain}/categories?offset=0&limit=100`,
  );
  const categoriesPayload = await categoriesResponse.json();
  const categories = categoriesPayload?.results ?? [];
  const poiResponse = await fetch(
    `${API_URL}/front/${domain}/points_of_interest?offset=0&limit=100`,
  );
  const poiPayload = poiResponse.ok ? await poiResponse.json() : {results: []};
  const marcos = (poiPayload?.results ?? [])
    .filter((item: any) => categoryIdsOf(item).includes(203))
    .sort((a: any, b: any) => Number(titleOf(b.title)) - Number(titleOf(a.title)));
  const glossaryCategory = categories.find((item: any) =>
    /gloss[aá]rio/i.test(`${item?.page_slug ?? ''} ${titleOf(item?.name)}`),
  );
  const glossaryResponse = glossaryCategory
    ? await fetch(
        `${API_URL}/front/${domain}/contents?offset=0&limit=100&categories.category.category_id=${glossaryCategory.category_id}`,
      )
    : undefined;
  const glossaryPayload = glossaryResponse?.ok
    ? await glossaryResponse.json()
    : {results: []};
  const contentItems =
    contents?.results ?? contents?.result ?? contents?.data ?? [];
  const menu = workspace?.app_settings?.app_menu_lines ?? [];
  // Warm the service-worker cache while the kiosk is online. These requests
  // complete before the initial load is considered synchronized.
  const offlineData = await warmOfflineCache(
    domain,
    contentItems,
    menu,
    categories,
    glossaryPayload?.results ?? [],
    glossaryCategory?.category_id,
  );
  const allContentItems = offlineData.contents?.length
    ? offlineData.contents
    : contentItems;
  const effectiveCategories = offlineData.categories?.length
    ? offlineData.categories
    : categories;
  const effectiveMarcos = offlineData.pointsOfInterest?.length
    ? offlineData.pointsOfInterest
        .filter((item: any) => categoryIdsOf(item).includes(203))
        .sort((a: any, b: any) => Number(titleOf(b.title)) - Number(titleOf(a.title)))
    : marcos;
  const effectiveGlossary = offlineData.glossary ?? glossaryPayload?.results ?? [];
  const effectiveScreensaver = findContent({results: allContentItems}, 'screensaver') ?? screensaver;
  const effectiveTouchhand = findContent({results: allContentItems}, 'touchhand') ?? touchhand;
  const effectiveScreensaverWaitTime = findContent({results: allContentItems}, 'screensaverwaittime') ?? screensaverWaitTime;
  const effectivePartnerLogos = findContent({results: allContentItems}, 'logosparceiros') ?? partnerLogos;
  const effectiveTexts = findContent({results: allContentItems}, 'textos') ?? texts;
  const effectivePhotography = findContent({results: allContentItems}, 'fotography') ?? photography;
  const menuLogoContents = (
    Array.isArray(allContentItems) ? allContentItems : []
  ).filter((item: any) =>
    String(item?.slug ?? item?.page_slug ?? '')
      .toLowerCase()
      .startsWith('logo-'),
  );
  const featured = contentImage(effectiveScreensaver);
  const galleryImages = (
    effectiveScreensaver?.galleries ??
    effectiveScreensaver?.gallery ??
    effectiveScreensaver?.images ??
    effectiveScreensaver?.files ??
    effectiveScreensaver?.media ??
    []
  )
    .flatMap((item: any) => [
      mediaUrl(item?.image_media),
      mediaUrl(item),
      mediaUrl(item?.image),
      mediaUrl(item?.file_image_media),
    ])
    .filter(Boolean);
  const images = [featured, ...galleryImages].filter(Boolean);
  const result = {
    workspaceId: Number(workspace?.workspace_id),
    domain,
    logo: workspace?.app_settings?.app_logo_url,
    banner: workspace?.app_settings?.app_banner_url,
    languages: (workspace?.languages ?? [])
      .map((entry: any) => ({
        tag: entry?.language?.tag,
        label:
          entry?.language?.tag === 'pt-PT'
            ? 'PT'
            : entry?.language?.tag === 'en-US'
              ? 'EN'
              : entry?.language?.local || entry?.language?.name,
      }))
      .filter((entry: any) => entry.tag),
    title:
      localized(workspace?.app_settings?.general?.title) ||
      localized(workspace?.name) ||
      'Findify',
    colors: workspace?.app_settings?.colors ?? {},
    subtitle: workspace?.app_settings?.general?.subtitle,
    menu,
    menuLogoContents,
    categories: effectiveCategories,
    contents: allContentItems,
    marcos: effectiveMarcos,
    glossary: effectiveGlossary,
    offlineData,
    logos: contentImage(effectivePartnerLogos),
    credits: {texts: effectiveTexts, photography: effectivePhotography},
    images,
    hand: contentImage(effectiveTouchhand),
    text: contentText(effectiveTouchhand),
    waitSeconds: Math.max(
      1,
      Number.parseInt(
        contentText(effectiveScreensaverWaitTime).replace(/[^0-9]/g, ''),
        10,
      ) || 60,
    ),
    _offlineSnapshot: false,
    offlineCacheStatus: {
      complete: offlineData.complete,
      persistentStorage: offlineData.persistentStorage,
      resourceCount: offlineData.resourceCount,
      errors: [...offlineData.errors],
    },
  };
  let snapshotSaved = false;
  try {
    await writeOfflineSnapshot(result);
    snapshotSaved = true;
  } catch (error) {
    offlineData.errors.push(
      `Snapshot IndexedDB: ${error instanceof Error ? error.message : 'falha ao guardar os dados'}`,
    );
  }
  const cacheComplete = offlineData.complete && snapshotSaved;
  result.offlineCacheStatus = {
    ...result.offlineCacheStatus,
    complete: cacheComplete,
    snapshotSaved,
    errors: [...offlineData.errors],
  };
  try {
    window.localStorage.setItem('findify-offline-cache-complete', String(cacheComplete));
  } catch {
    // A local status marker is helpful but is not part of the offline snapshot.
  }
  window.dispatchEvent(
    new CustomEvent('findify-cache-progress', {
      detail: cacheComplete
        ? {
            percent: 100,
            label: offlineData.persistentStorage
              ? `Cache offline concluída (${offlineData.resourceCount} recursos verificados)`
              : `Cache concluída (${offlineData.resourceCount} recursos); armazenamento persistente não concedido`,
          }
        : {
            percent: 99,
            label: `Atualização incompleta: ${offlineData.errors.length} falhas; verificar antes de desligar`,
          },
    }),
  );
  return result;
};

const getData = async () => {
  try {
    return await getNetworkData();
  } catch (networkError) {
    const cached = await readOfflineSnapshot();
    if (cached) return {...cached, _offlineSnapshot: true};
    throw networkError;
  }
};

export const KioskApp = () => {
  const [data, setData] = useState<any>(null);
  const [started, setStarted] = useState(false);
  const [screen, setScreen] = useState<
    | 'home'
    | 'category'
    | 'routes'
    | 'route-detail'
    | 'detail'
    | 'credits'
    | 'quiz-intro'
    | 'quiz-question'
    | 'quiz-result'
    | '3d'
  >('home');
  const [selectedMenu, setSelectedMenu] = useState<any>(null);
  const navigationRequestRef = useRef(0);
  const modelViewerRef = useRef<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedMarcoIndex, setSelectedMarcoIndex] = useState(8);
  const [selectedMarcoImageIndex, setSelectedMarcoImageIndex] = useState(0);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [glossaryPage, setGlossaryPage] = useState(0);
  const glossarySwipeStartRef = useRef<{x: number; pointerId: number} | null>(null);
  const [collectionPeriod, setCollectionPeriod] = useState<string>('');
  const [collectionTypology, setCollectionTypology] = useState<string>('');
  const [selectedFloorId, setSelectedFloorId] = useState<string>('');
  const [routes, setRoutes] = useState<any[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<any>(null);
  const [selectedRoutePoint, setSelectedRoutePoint] = useState<any>(null);
  const [routeTooltipPosition, setRouteTooltipPosition] = useState({
    x: 0,
    y: 0,
  });
  const [routeTooltipSide, setRouteTooltipSide] = useState<'left' | 'right'>(
    'right',
  );
  const routeMapRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = useState('pt-PT');
  const [error, setError] = useState('A carregar…');
  const [quiz, setQuiz] = useState<any>(null);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswer, setQuizAnswer] = useState<string | null>(null);
  const [quizFeedback, setQuizFeedback] = useState<any>(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizHistory, setQuizHistory] = useState<any[]>([]);
  const [quizRemaining, setQuizRemaining] = useState(0);
  const [quizExitConfirm, setQuizExitConfirm] = useState(false);
  const [quizError, setQuizError] = useState('');
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizAnonymousId, setQuizAnonymousId] = useState('');
  const [quizResponses, setQuizResponses] = useState<any[]>([]);
  const [quizStartedAt, setQuizStartedAt] = useState(0);
  const [quizSubmission, setQuizSubmission] = useState<any>(null);
  const [quizSelectedIds, setQuizSelectedIds] = useState<string[]>([]);
  const [quizTextAnswer, setQuizTextAnswer] = useState('');
  const [quizGlobalTimedOut, setQuizGlobalTimedOut] = useState(false);
  const [quizGlobalDeadline, setQuizGlobalDeadline] = useState<number | null>(
    null,
  );
  const selectedMarcoImages =
    data?.workspaceId === 203 && selectedItem
      ? contentImages(selectedItem, language)
      : [];
  const glossaryItems = [...(data?.glossary ?? [])].sort(
    (a: any, b: any) => Number(a?.content_id ?? 0) - Number(b?.content_id ?? 0),
  );
  const glossaryPageSize = 4;
  const glossaryPageCount = Math.max(1, Math.ceil(glossaryItems.length / glossaryPageSize));

  useEffect(() => {
    if (!glossaryOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setGlossaryOpen(false);
      if (event.key === 'ArrowRight') setGlossaryPage(page => Math.min(glossaryPageCount - 1, page + 1));
      if (event.key === 'ArrowLeft') setGlossaryPage(page => Math.max(0, page - 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [glossaryOpen, glossaryPageCount]);

  useEffect(() => {
    setSelectedMarcoImageIndex(0);
  }, [selectedItem?.point_of_interest_id, selectedItem?.content_id]);

  useEffect(() => {
    if (screen !== 'detail' || selectedMarcoImages.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setSelectedMarcoImageIndex(index => (index + 1) % selectedMarcoImages.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [screen, selectedItem?.point_of_interest_id, selectedItem?.content_id, selectedMarcoImages.length]);

  useEffect(() => {
    getData()
      .then(nextData => {
        if (navigator.storage?.persist) void navigator.storage.persist();
        updateBrowserIdentity(nextData.title, nextData.logo);
        updateTheme(nextData.colors);
        setData(nextData);
        if (navigator.onLine && !nextData._offlineSnapshot && nextData.offlineCacheStatus?.complete)
          window.localStorage.setItem(
            'findify-last-content-sync',
            new Date().toISOString(),
          );
      })
      .catch(reason =>
        setError(
          reason instanceof Error
            ? reason.message
            : 'Não foi possível carregar o ecrã.',
        ),
      );
  }, []);

  useEffect(() => {
    const forceRefresh = async () => {
      try {
        const nextData = await getNetworkData();
        updateBrowserIdentity(nextData.title, nextData.logo);
        updateTheme(nextData.colors);
        setData(nextData);
        if (!nextData.offlineCacheStatus?.complete) {
          const failures = nextData.offlineCacheStatus?.errors ?? [];
          window.dispatchEvent(
            new CustomEvent('findify-cache-refresh-failed', {
              detail: {
                message: failures.slice(0, 3).join(' · ') ||
                  'A cache offline não ficou completa. Mantenha o dispositivo ligado à rede e tente novamente.',
              },
            }),
          );
          return;
        }
        window.localStorage.setItem(
          'findify-last-content-sync',
          new Date().toISOString(),
        );
        window.localStorage.setItem('findify-offline-cache-complete', 'true');
        window.dispatchEvent(new Event('findify-cache-refresh-complete'));
      } catch (reason) {
        window.dispatchEvent(
          new CustomEvent('findify-cache-refresh-failed', {
            detail: {
              message:
                reason instanceof Error
                  ? reason.message
                  : 'Não foi possível atualizar a cache offline.',
            },
          }),
        );
      }
    };
    window.addEventListener('findify-force-content-refresh', forceRefresh);
    return () =>
      window.removeEventListener(
        'findify-force-content-refresh',
        forceRefresh,
      );
  }, []);

  useEffect(() => {
    if (!data) return undefined;
    let syncing = false;
    const sync = async () => {
      if (!navigator.onLine || syncing) return;
      syncing = true;
      try {
        const nextData = await getNetworkData();
        updateBrowserIdentity(nextData.title, nextData.logo);
        updateTheme(nextData.colors);
        setData(nextData);
        if (nextData.offlineCacheStatus?.complete) {
          window.localStorage.setItem(
            'findify-last-content-sync',
            new Date().toISOString(),
          );
          window.localStorage.setItem('findify-offline-cache-complete', 'true');
        }
      } catch {
        // Keep the current snapshot when a background sync fails.
      } finally {
        syncing = false;
      }
    };
    const interval = window.setInterval(() => void sync(), 6 * 60 * 60 * 1000);
    window.addEventListener('online', sync);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', sync);
    };
  }, [data]);

  useEffect(() => {
    if (!data || !started) return undefined;

    let timeout: number | undefined;
    const returnToScreensaver = () => {
      setStarted(false);
      setScreen('home');
      setSelectedMenu(null);
      setSelectedItem(null);
      setItems([]);
      setQuiz(null);
      setQuizExitConfirm(false);
    };
    const restartTimer = () => {
      if (timeout) window.clearTimeout(timeout);
      timeout = window.setTimeout(returnToScreensaver, data.waitSeconds * 1000);
    };
    const activityEvents = ['pointerdown', 'touchstart', 'keydown'];
    activityEvents.forEach(eventName =>
      window.addEventListener(eventName, restartTimer, {passive: true}),
    );
    restartTimer();
    return () => {
      if (timeout) window.clearTimeout(timeout);
      activityEvents.forEach(eventName =>
        window.removeEventListener(eventName, restartTimer),
      );
    };
  }, [data, started, screen]);
  useEffect(() => {
    if (
      screen !== 'quiz-question' ||
      !quiz ||
      !currentQuestion ||
      quizAnswer !== null ||
      quizGlobalTimedOut
    )
      return undefined;
    setQuizRemaining(currentQuestion.timeLimit || quiz.timeLimit || 25);
    const timer = window.setInterval(
      () =>
        setQuizRemaining(value => {
          if (value <= 1) {
            window.clearInterval(timer);
            window.setTimeout(() => timeoutQuizQuestion(false), 0);
            return 0;
          }
          return value - 1;
        }),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [screen, quiz, quizIndex, quizAnswer, quizGlobalTimedOut]);

  useEffect(() => {
    if (screen !== 'quiz-question' || !quizGlobalDeadline || quizGlobalTimedOut)
      return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() >= quizGlobalDeadline) {
        window.clearInterval(timer);
        markGlobalTimeout();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [screen, quizGlobalDeadline, quizGlobalTimedOut]);
  useEffect(() => {
    if (screen !== 'routes' || !routeMapRef.current || !selectedRoute) return;
    const points = (selectedRoute.points_of_interest ?? [])
      .slice()
      .sort(
        (a: any, b: any) =>
          Number(a.sequence ?? a.position ?? a.order ?? 0) -
          Number(b.sequence ?? b.position ?? b.order ?? 0),
      )
      .map((entry: any) => entry.point_of_interest ?? entry)
      .filter((point: any) => point.lat_long);
    const coordinates = points
      .map((point: any) => {
        const match = String(point.lat_long).match(
          /POINT\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i,
        );
        return match
          ? ([Number(match[1]), Number(match[2])] as [number, number])
          : null;
      })
      .filter(Boolean) as [number, number][];
    if (!coordinates.length) return;
    const map = L.map(routeMapRef.current, {zoomControl: false}).setView(
      coordinates[0],
      15,
    );
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: '© Esri',
      },
    ).addTo(map);
    map.on('click', () => setSelectedRoutePoint(null));
    coordinates.forEach((coordinate, index) =>
      L.marker(coordinate, {
        icon: L.divIcon({
          className: 'route-leaflet-marker',
          html: `<span>${index + 1}</span><b>${titleOf(points[index].title ?? points[index].name, language)}</b>`,
        }),
      })
        .addTo(map)
        .on('click', event => {
          L.DomEvent.stopPropagation(event.originalEvent);
          const position = map.latLngToContainerPoint(event.latlng);
          const mapSize = map.getSize();
          const tooltipWidth = mapSize.x * 0.24;
          const opensLeft = position.x + tooltipWidth + 48 > mapSize.x;
          const x = opensLeft
            ? Math.max(tooltipWidth + 24, position.x - tooltipWidth - 24)
            : Math.min(mapSize.x - tooltipWidth - 24, position.x + 24);
          const y = Math.max(90, Math.min(mapSize.y - 90, position.y));
          setRouteTooltipSide(opensLeft ? 'left' : 'right');
          setRouteTooltipPosition({x, y});
          setSelectedRoutePoint(points[index]);
        }),
    );
    map.fitBounds(L.latLngBounds(coordinates), {padding: [40, 40]});
    return () => map.remove();
  }, [screen, selectedRoute]);
  if (!data)
    return (
      <main className="kiosk kiosk--loading">
        <p>{error}</p>
      </main>
    );
  if (!started)
    return (
      <main
        className="kiosk"
        onClick={() => setStarted(true)}
        role="button"
        tabIndex={0}>
        <div className="kiosk__background">
          {data.images.map((image: string, index: number) => (
            <img
              key={image}
              src={image}
              alt=""
              className="kiosk__image"
              style={{animationDelay: `${index * 8}s`}}
            />
          ))}
        </div>
        {data.logos && (
          <img className="kiosk__partners" src={data.logos} alt="" />
        )}
        {data.logo && (
          <img className="kiosk__logo" src={data.logo} alt={data.title} />
        )}
        <div className="kiosk__prompt">
          {data.hand && <img src={data.hand} alt="" className="kiosk__hand" />}
          <span>{data.text}</span>
        </div>
      </main>
    );
  const openCategory = async (menu: any) => {
    const requestId = ++navigationRequestRef.current;
    const isCurrentRequest = () =>
      requestId === navigationRequestRef.current;

    // Do not leave the previous category visible while the new menu is
    // loading. More importantly, older pending requests must not be allowed
    // to restore their menu/layout after this click.
    setSelectedMenu(null);
    setSelectedItem(null);
    setItems([]);

    const categoryId = menu.content_category;
    const menuCategory = data.categories.find(
      (item: any) => String(item.category_id) === String(categoryId),
    );
    const opensDirectory = /diret[oó]rio do edif[ií]cio/i.test(
      `${titleOf(menu.label, language)} ${titleOf(menuCategory?.name, language)}`,
    );
    if (opensDirectory) {
      const floorRoot = data.categories.find((item: any) =>
        /^(pisos|floors)$/i.test(String(item.page_slug ?? item.slug ?? '')),
      );
      const firstFloor = data.categories
        .filter(
          (item: any) =>
            String(item.parent_category_id ?? item.parent_id) ===
            String(floorRoot?.category_id),
        )
        .sort(
          (a: any, b: any) =>
            (Date.parse(
              b.publish_date ?? b.public_date ?? b.make_public_on ?? '',
            ) || 0) -
            (Date.parse(
              a.publish_date ?? a.public_date ?? a.make_public_on ?? '',
            ) || 0),
        )[0];
      setSelectedFloorId(String(firstFloor?.category_id ?? ''));
      setSelectedMenu(menu);
      setScreen('category');
      return;
    }
    if (
      menu.content_type === 'contents' &&
      !/pontos de interesse|roteiros|routes/i.test(
        titleOf(menu.label, language),
      )
    ) {
      try {
        const payload = await fetchJsonWithOfflineCache(
          `${API_URL}/front/${data.domain}/contents?offset=0&limit=100&categories.category.category_id=${categoryId}`,
        );
        if (!isCurrentRequest()) return;
        setItems(payload?.results ?? []);
      } catch {
        if (!isCurrentRequest()) return;
        setItems(data.offlineData?.contentsByCategory?.[String(categoryId)] ?? []);
      }
      if (!isCurrentRequest()) return;
      setSelectedMenu(menu);
      setScreen('category');
      return;
    }
    const category = data.categories.find(
      (item: any) => String(item.category_id) === String(categoryId),
    );
    const isCollection = /cole[cç]ão|esp[oó]lio|colection|collection/i.test(
      `${titleOf(menu.label, language)} ${category?.page_slug ?? ''} ${titleOf(category?.name, language)}`,
    );
    const isRoutes = /pontos de interesse|roteiros|routes/i.test(
      `${titleOf(menu.label, language)} ${category?.page_slug ?? ''} ${titleOf(category?.name, language)}`,
    );
    if (isRoutes) {
      let summaries;
      try {
        const payload = await fetchJsonWithOfflineCache(
          `${API_URL}/front/${data.domain}/routes?offset=0&limit=100`,
        );
        if (!isCurrentRequest()) return;
        summaries = (payload?.results ?? []).filter(
          (route: any) => route.published !== false,
        );
      } catch {
        if (!isCurrentRequest()) return;
        setRoutes(data.offlineData?.routes ?? []);
        setSelectedRoute(data.offlineData?.routes?.[0] ?? null);
        setSelectedRoutePoint(null);
        setSelectedMenu(menu);
        setScreen('routes');
        return;
      }
      summaries = summaries.filter(
        (route: any) => route.published !== false,
      );
      const details = await Promise.all(
        summaries.map(async (route: any) => {
          const id = route.route_id ?? route.page_slug;
          try {
            const detail = await fetch(
              `${API_URL}/front/${data.domain}/routes/${encodeURIComponent(id)}`,
            );
            return detail.ok ? detail.json() : route;
          } catch {
            return route;
          }
        }),
      );
      if (!isCurrentRequest()) return;
      setRoutes(details);
      setSelectedRoute(details[0] ?? null);
      setSelectedRoutePoint(null);
      setSelectedMenu(menu);
      setScreen('routes');
      return;
    }
    if (isCollection) {
      const collectionRoot = data.categories.find((item: any) =>
        /colection|collection/i.test(
          String(item?.page_slug ?? item?.slug ?? ''),
        ),
      );
      const allowedIds = descendantCategoryIds(
        collectionRoot?.category_id ?? categoryId,
        data.categories,
      );
      // The public API does not return descendants when filtering by the root
      // category. Load the POIs once and apply the complete descendant set here.
      let collectionItems;
      try {
        const payload = await fetchJsonWithOfflineCache(
          `${API_URL}/front/${data.domain}/points_of_interest?offset=0&limit=100`,
        );
        if (!isCurrentRequest()) return;
        collectionItems = (payload?.results ?? []).filter((item: any) =>
          categoryIdsOf(item).some((id: number) => allowedIds.has(id)),
        );
      } catch {
        if (!isCurrentRequest()) return;
        collectionItems = (data.offlineData?.pointsOfInterest ?? []).filter(
          (item: any) => categoryIdsOf(item).some((id: number) => allowedIds.has(id)),
        );
      }
      if (!isCurrentRequest()) return;
      // Some public responses omit category relations on the content list. In
      // that case keep the collection usable and let the detail endpoint supply
      // the complete record.
      setItems(
        collectionItems.map((item: any) => ({
          ...item,
          name: item.title ?? item.name,
        })),
      );
      setSelectedMenu(menu);
      setCollectionPeriod('');
      setCollectionTypology('');
      setScreen('category');
      return;
    }
    try {
      const payload = await fetchJsonWithOfflineCache(
        `${API_URL}/front/${data.domain}/points_of_interest?offset=0&limit=100&categories.category.category_id=${categoryId}`,
      );
      if (!isCurrentRequest()) return;
      setItems(payload?.results ?? []);
    } catch {
      if (!isCurrentRequest()) return;
      setItems(
        (data.offlineData?.pointsOfInterest ?? []).filter((item: any) =>
          categoryIdsOf(item).includes(Number(categoryId)),
        ),
      );
    }
    if (!isCurrentRequest()) return;
    setSelectedMenu(menu);
    setScreen('category');
  };
  const openContent = async (menu: any) => {
    const requestId = ++navigationRequestRef.current;
    setSelectedMenu(null);
    setSelectedItem(null);
    setItems([]);
    let content;
    try {
      content = await fetchJsonWithOfflineCache(
        `${API_URL}/front/${data.domain}/contents/${encodeURIComponent(menu.content_id)}`,
      );
      if (requestId !== navigationRequestRef.current) return;
    } catch {
      if (requestId !== navigationRequestRef.current) return;
      content = data.contents?.find(
        (item: any) =>
          String(item.content_id ?? item.id) === String(menu.content_id),
      );
      if (!content)
        throw new Error('Não foi possível carregar o conteúdo offline.');
    }
    if (requestId !== navigationRequestRef.current) return;
    setSelectedMenu(menu);
    setSelectedItem(content);
    setScreen('detail');
  };
  const resetQuiz = () => {
    setQuiz(null);
    setQuizIndex(0);
    setQuizAnswer(null);
    setQuizFeedback(null);
    setQuizScore(0);
    setQuizHistory([]);
    setQuizRemaining(0);
    setQuizExitConfirm(false);
    setQuizError('');
    setQuizAnonymousId('');
    setQuizResponses([]);
    setQuizStartedAt(0);
    setQuizSubmission(null);
    setQuizSelectedIds([]);
    setQuizTextAnswer('');
    setQuizGlobalTimedOut(false);
    setQuizGlobalDeadline(null);
  };
  const openQuiz = async (menu: any, slug: string) => {
    setQuizLoading(true);
    setQuizError('');
    try {
      const nextQuiz = await fetchQuiz(data.domain, slug, language);
      if (!nextQuiz) throw new Error('Não foi possível carregar este quiz.');
      setSelectedMenu(menu);
      setQuiz(nextQuiz);
      setQuizIndex(0);
      setQuizAnswer(null);
      setQuizFeedback(null);
      setQuizScore(0);
      setQuizHistory([]);
      setQuizResponses([]);
      setQuizSubmission(null);
      setQuizSelectedIds([]);
      setQuizTextAnswer('');
      setScreen('quiz-intro');
    } catch (reason) {
      setQuizError(
        reason instanceof Error
          ? reason.message
          : 'Não foi possível carregar o quiz.',
      );
    } finally {
      setQuizLoading(false);
    }
  };
  const leaveQuiz = () => {
    resetQuiz();
    setSelectedMenu(null);
    setScreen('home');
  };
  const abandonQuiz = () => {
    setQuizIndex(0);
    setQuizAnswer(null);
    setQuizFeedback(null);
    setQuizSelectedIds([]);
    setQuizTextAnswer('');
    setQuizExitConfirm(false);
    setScreen('quiz-intro');
  };
  const quizAbandonmentMessage =
    localized((quiz as any)?.abandonmentMessage, language) ||
    'O seu progresso não será guardado e vai voltar ao menu principal.';
  const startQuiz = () => {
    const globalLimit = Number(
      (quiz as any)?.globalTimeLimit ??
        (quiz as any)?.totalTimeLimit ??
        (quiz as any)?.duration ??
        0,
    );
    setQuizIndex(0);
    setQuizAnswer(null);
    setQuizFeedback(null);
    setQuizScore(0);
    setQuizHistory([]);
    setQuizResponses([]);
    setQuizSubmission(null);
    setQuizSelectedIds([]);
    setQuizTextAnswer('');
    setQuizGlobalTimedOut(false);
    setQuizGlobalDeadline(
      globalLimit > 0 ? Date.now() + globalLimit * 1000 : null,
    );
    setQuizAnonymousId(
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
    setQuizStartedAt(Date.now());
    setScreen('quiz-question');
  };

  const timeoutQuizQuestion = (globalTimeout: boolean) => {
    if (!quiz || !currentQuestion || quizAnswer !== null) return;
    const ids = quizSelectedIds.map(String);
    const correctIds = currentQuestion.options
      .filter((option: any) => option.correct)
      .map((option: any) => String(option.id));
    const answer = {
      question_id: Number(currentQuestion.id),
      answer_ids: ids.map(Number).filter(Number.isFinite),
      timed_out: true,
      global_timeout: globalTimeout,
      ...(currentQuestion.type === 'text' ? {text_answer: quizTextAnswer} : {}),
    };
    setQuizResponses(previous => [...previous, answer]);
    setQuizAnswer('__timeout__');
    setQuizFeedback({
      correct: false,
      timedOut: true,
      globalTimeout,
      selectedIds: ids,
      correctIds,
      feedbackTitle: globalTimeout
        ? (quiz as any).globalTimeoutMessage || 'Tempo esgotado!'
        : (currentQuestion as any).timeoutMessage ||
          (quiz as any).questionTimeoutMessage ||
          'Tempo esgotado!',
      explanation: globalTimeout
        ? (quiz as any).globalTimeoutExplanation ||
          'Já lá vai o tempo! Continue para concluir o quiz.'
        : (currentQuestion as any).timeoutExplanation ||
          (quiz as any).questionTimeoutExplanation ||
          'Já lá vai o tempo!',
    });
    setQuizHistory(history => [
      ...history,
      {
        questionId: currentQuestion.id,
        selectedIds: ids,
        correct: false,
        timedOut: true,
        globalTimeout,
      },
    ]);
    if (globalTimeout) setQuizGlobalTimedOut(true);
    void submitQuiz([...quizResponses, answer], false).catch(() => undefined);
  };

  const markGlobalTimeout = () => {
    if (!quiz || quizGlobalTimedOut) return;
    setQuizGlobalTimedOut(true);
    setQuizRemaining(0);
    if (quizAnswer === null) timeoutQuizQuestion(true);
    else
      setQuizFeedback((previous: any) => ({
        ...(previous || {}),
        timedOut: true,
        globalTimeout: true,
        correct: false,
        feedbackTitle: (quiz as any).globalTimeoutMessage || 'Tempo esgotado!',
        explanation:
          (quiz as any).globalTimeoutExplanation ||
          'Já lá vai o tempo! Continue para concluir o quiz.',
      }));
  };
  const currentQuestion = quiz?.questions?.[quizIndex];
  const quizQuestionDuration = Number(
    currentQuestion?.timeLimit || quiz?.timeLimit || 25,
  );
  const quizTimeProgress = Math.max(
    0,
    Math.min(
      100,
      quizQuestionDuration ? (quizRemaining / quizQuestionDuration) * 100 : 0,
    ),
  );
  const quizMaximumScore = Number(
    (quiz as any)?.maximumScore ??
      (quiz as any)?.maximum_score ??
      (quiz as any)?.maxScore ??
      quiz?.questions?.reduce(
        (total: number, question: any) =>
          total +
          Number(
            question.points ||
              question.options.reduce(
                (sum: number, option: any) =>
                  sum + Number(option.points ?? option.score ?? 0),
                0,
              ),
          ),
        0,
      ) ??
      0,
  );
  const quizCategoryName = (category: any) => {
    const categoryId = category?.id ?? category?.category_id ?? category;
    const workspaceCategory = data.categories.find(
      (item: any) => String(item.category_id ?? item.id) === String(categoryId),
    );
    return localized(
      category?.name ||
        category?.title ||
        workspaceCategory?.name ||
        workspaceCategory?.title ||
        workspaceCategory?.label ||
        categoryId ||
        'Categoria',
      language,
    );
  };
  const quizCategoryScores = (quiz?.questions || []).reduce(
    (
      groups: Record<string, {name: string; score: number}>,
      question: any,
      index: number,
    ) => {
      const category =
        question.category || question.category_name || question.categoryName;
      const key = String(
        category?.id ?? category?.slug ?? category ?? `category-${index}`,
      );
      const name = quizCategoryName(category);
      const response = quizHistory.find(
        (item: any) => Number(item.questionId) === Number(question.id),
      );
      const selected = response?.selectedIds?.map(String) || [];
      const score = question.options
        .filter((option: any) => selected.includes(String(option.id)))
        .reduce(
          (sum: number, option: any) =>
            sum + Number(option.points ?? option.score ?? 0),
          0,
        );
      groups[key] = groups[key] || {name, score: 0};
      groups[key].score += score;
      return groups;
    },
    {},
  );
  const submitQuiz = async (responses: any[], store = true) => {
    if (!quiz || !quizAnonymousId) return undefined;
    const response = await fetch(
      `${API_URL}/quizzes/public/${encodeURIComponent(quiz.slug)}/submit?locale=${encodeURIComponent(language)}`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          anonymous_id: quizAnonymousId,
          answers: responses,
          total_time: quizStartedAt
            ? Math.round((Date.now() - quizStartedAt) / 1000)
            : 0,
        }),
      },
    );
    if (!response.ok)
      throw new Error(
        `Não foi possível submeter a resposta (${response.status}).`,
      );
    const submission = await response.json();
    if (store) setQuizSubmission(submission);
    return submission;
  };
  const finalizeQuizAnswer = (ids: string[]) => {
    if (!quiz || !currentQuestion || quizAnswer !== null) return;
    const correctIds = currentQuestion.options
      .filter((option: any) => option.correct)
      .map((option: any) => String(option.id));
    const knownCorrect = correctIds.length > 0;
    const isCorrect =
      knownCorrect &&
      ids.length === correctIds.length &&
      ids.every(id => correctIds.includes(String(id)));
    const selectedOptions = currentQuestion.options.filter((option: any) =>
      ids.map(String).includes(String(option.id)),
    );
    const rightOptions = selectedOptions.filter(
      (option: any) => option.correct,
    );
    const wrongOptions = selectedOptions.filter(
      (option: any) => !option.correct,
    );
    const feedbackOptions = wrongOptions.length ? wrongOptions : rightOptions;
    const feedbackTitle =
      feedbackOptions
        .map((option: any) =>
          isCorrect ? option.correctFeedback : option.incorrectFeedback,
        )
        .filter(Boolean)
        .join('\n\n') || (isCorrect ? 'Certo!' : 'Não é bem assim.');
    const feedbackExplanation =
      feedbackOptions
        .map((option: any) => option.explanation)
        .filter(Boolean)
        .join('\n\n') ||
      currentQuestion.explanation ||
      '';
    const answer = {
      question_id: Number(currentQuestion.id),
      answer_ids: ids.map(Number).filter(Number.isFinite),
      ...(currentQuestion.type === 'text' ? {text_answer: quizTextAnswer} : {}),
    };
    setQuizResponses(previous => [...previous, answer]);
    setQuizAnswer(ids[0] ?? '__timeout__');
    setQuizSelectedIds(ids);
    setQuizFeedback({
      correct: knownCorrect ? isCorrect : null,
      selectedIds: ids,
      correctIds,
      feedbackTitle,
      explanation: feedbackExplanation,
    });
    if (knownCorrect && isCorrect)
      setQuizScore(score => score + (currentQuestion.points || 1));
    setQuizHistory(history => [
      ...history,
      {questionId: currentQuestion.id, selectedIds: ids, correct: isCorrect},
    ]);
    const nextResponses = [...quizResponses, answer];
    void submitQuiz(nextResponses, false)
      .then(submission => {
        const apiFeedback = submission?.feedback?.find(
          (entry: any) =>
            String(entry.question_id) === String(currentQuestion.id),
        );
        if (apiFeedback)
          setQuizFeedback((previous: any) => ({
            ...previous,
            correct: previous?.correct ?? Boolean(apiFeedback.correct),
            feedbackTitle:
              previous?.feedbackTitle ||
              titleOf(
                apiFeedback.correct_feedback ??
                  apiFeedback.incorrect_feedback ??
                  apiFeedback.feedback,
                language,
              ),
            explanation:
              previous?.explanation ||
              titleOf(apiFeedback.explanation ?? apiFeedback.message, language),
          }));
      })
      .catch(() => undefined);
  };
  const answerQuiz = (optionId: string) => {
    if (!quiz || !currentQuestion || quizAnswer !== null) return;
    if (currentQuestion.type === 'multiple_choice') {
      setQuizSelectedIds(ids =>
        ids.includes(optionId)
          ? ids.filter(id => id !== optionId)
          : [...ids, optionId],
      );
    } else if (currentQuestion.type !== 'text') finalizeQuizAnswer([optionId]);
  };
  const submitSelectedQuizAnswer = () => {
    if (!currentQuestion || quizAnswer !== null) return;
    finalizeQuizAnswer(currentQuestion.type === 'text' ? [] : quizSelectedIds);
  };
  const continueQuiz = async () => {
    if (!quiz) return;
    if (quizIndex >= quiz.questions.length - 1) {
      try {
        await submitQuiz(quizResponses);
      } catch {
        /* retain local result if offline */
      }
      setScreen('quiz-result');
    } else {
      const nextIndex = quizIndex + 1;
      setQuizIndex(nextIndex);
      setQuizSelectedIds([]);
      setQuizTextAnswer('');
      if (quizGlobalTimedOut) {
        setQuizRemaining(0);
        setQuizAnswer('__timeout__');
        setQuizFeedback({
          correct: false,
          timedOut: true,
          globalTimeout: true,
          selectedIds: [],
          correctIds: [],
          feedbackTitle:
            (quiz as any).globalTimeoutMessage || 'Tempo esgotado!',
          explanation:
            (quiz as any).globalTimeoutExplanation ||
            'Já lá vai o tempo! Continue para concluir o quiz.',
        });
      } else {
        setQuizRemaining(
          quiz.questions[nextIndex]?.timeLimit || quiz.timeLimit || 25,
        );
        setQuizAnswer(null);
        setQuizFeedback(null);
      }
    }
  };
  const categoryFor = (menu: any) =>
    data.categories.find(
      (category: any) =>
        String(category.category_id ?? category.id) ===
        String(menu.content_category),
    );
  const menuLogo = (menu: any) => {
    const category = categoryFor(menu);
    const slugify = (value: any) =>
      String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const categorySlug = slugify(
      category?.slug ??
        category?.page_slug ??
        category?.category_slug ??
        category?.category?.slug ??
        category?.category?.page_slug,
    );
    const labelSlug = slugify(localized(menu?.label, language));
    const logo = data.menuLogoContents.find((content: any) => {
      const contentSlug = slugify(
        content?.slug ?? content?.page_slug ?? content?.name,
      );
      return (
        contentSlug === `logo-${categorySlug}` ||
        contentSlug === `logo-${labelSlug}`
      );
    });
    return contentImage(logo);
  };
  const menuLabel = (menu: any) => titleOf(menu.label, language);
  const isCollection = (menu: any) =>
    /cole[cç]ão|esp[oó]lio|colection|collection/i.test(
      `${titleOf(menu?.label, language)} ${menu?.url ?? ''}`,
    );
  const collectionField = (content: any, name: string) => {
    const aliases: Record<string, string[]> = {
      capacidade: ['capacidade', 'capacity'],
      horário: ['horário', 'horario', 'schedule', 'time'],
      endereço: ['endereço', 'endereco', 'address'],
      notas: ['notas', 'notes'],
    };
    const sources = [content, content?.custom_fields, content?.fields].filter(
      Boolean,
    );
    for (const source of sources) {
      for (const key of aliases[name] ?? [name]) {
        const value = localized(source?.[key], language);
        if (value !== undefined && value !== null && value !== '') return value;
      }
    }
    return '';
  };
  const collectionChildren = (categorySlug: string) => {
    const parent = data.categories.find(
      (item: any) =>
        String(item.page_slug ?? item.slug ?? '').toLowerCase() ===
        categorySlug,
    );
    return data.categories.filter(
      (item: any) =>
        String(
          item.parent_id ?? item.parent_category_id ?? item.parent?.category_id,
        ) === String(parent?.category_id),
    );
  };
  const renderLanguages = () => (
    <div className="museum__languages">
      {(data.languages?.length
        ? data.languages
        : [
            {tag: 'pt-PT', label: 'PT'},
            {tag: 'en-US', label: 'EN'},
          ]
      ).map((entry: any) => (
        <button
          key={entry.tag}
          onClick={() => setLanguage(entry.tag)}
          className={language === entry.tag ? 'active' : ''}>
          {entry.label}
        </button>
      ))}
    </div>
  );
  const renderGlossaryModal = () => glossaryOpen ? (
    <div className="glossary-modal__backdrop" onClick={() => setGlossaryOpen(false)}>
      <section className="glossary-modal" role="dialog" aria-modal="true" aria-label={language === 'en-US' ? 'Glossary' : 'Glossário'} onClick={event => event.stopPropagation()}>
        <header className="glossary-modal__header"><h2>{language === 'en-US' ? 'Glossary' : 'Glossário'}</h2><button aria-label={language === 'en-US' ? 'Close glossary' : 'Fechar glossário'} onClick={() => setGlossaryOpen(false)}>×</button></header>
        <div className="glossary-modal__viewport" onPointerDown={event => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          glossarySwipeStartRef.current = {x: event.clientX, pointerId: event.pointerId};
          event.currentTarget.setPointerCapture(event.pointerId);
        }} onPointerUp={event => {
          const start = glossarySwipeStartRef.current;
          const delta = start ? event.clientX - start.x : 0;
          glossarySwipeStartRef.current = null;
          if (!start || start.pointerId !== event.pointerId || Math.abs(delta) < 50) return;
          setGlossaryPage(page => Math.max(0, Math.min(glossaryPageCount - 1, page + (delta < 0 ? 1 : -1))));
        }} onPointerCancel={() => { glossarySwipeStartRef.current = null; }} onLostPointerCapture={() => { glossarySwipeStartRef.current = null; }}>
          <div className="glossary-modal__track" style={{transform: `translateX(-${glossaryPage * (100 / glossaryPageCount)}%)`, width: `${glossaryPageCount * 100}%`}}>
            {Array.from({length: glossaryPageCount}, (_, pageIndex) => <div className="glossary-modal__page" key={pageIndex} style={{width: `${100 / glossaryPageCount}%`}}>
              <div className="glossary-modal__grid">{glossaryItems.slice(pageIndex * glossaryPageSize, (pageIndex + 1) * glossaryPageSize).map((item: any) => <article className="glossary-modal__card" key={item.content_id ?? item.id}><strong>{glossaryTitle(item, language)}</strong><p>{glossaryText(item, language)}</p></article>)}</div>
            </div>)}
          </div>
        </div>
        <nav className="glossary-modal__progress" aria-label={language === 'en-US' ? 'Glossary pages' : 'Páginas do glossário'}>
          <div className="glossary-modal__progress-track"><span style={{width: `${glossaryPageCount > 1 ? 100 / glossaryPageCount : 20}%`, transform: `translateX(${glossaryPage * 100}%)`}} /></div>
          <button className="glossary-modal__previous" aria-label={language === 'en-US' ? 'Previous glossary page' : 'Página anterior'} disabled={glossaryPage <= 0} onClick={() => setGlossaryPage(page => Math.max(0, page - 1))} />
          <button className="glossary-modal__next" aria-label={language === 'en-US' ? 'Next glossary page' : 'Página seguinte'} disabled={glossaryPage >= glossaryPageCount - 1} onClick={() => setGlossaryPage(page => Math.min(glossaryPageCount - 1, page + 1))} />
        </nav>
      </section>
    </div>
  ) : null;
  const back = () => {
    setScreen(screen === 'detail' ? 'category' : 'home');
    setSelectedItem(null);
  };
  const renderChrome = (
    children: React.ReactNode,
    heading = localized(data.subtitle, language),
    showBack = screen !== 'home' && screen !== 'credits',
  ) => (
    <>
      <div className="museum__top">
        {showBack && (
          <button onClick={back} className="museum__back">
            ‹
          </button>
        )}
        <div className="museum__heading">
          <strong>{heading}</strong>
        </div>
        {data.logos && (
          <img src={data.logos} className="museum__partners" alt="" />
        )}
      </div>
      {children}
      {renderLanguages()}
    </>
  );
  const quizImage =
    screen === 'quiz-question'
      ? quizMedia(currentQuestion?.image) || quizMedia(quiz?.image)
      : screen === 'quiz-result'
        ? quizMedia((quiz as any)?.finalImage) || quizMedia(quiz?.image)
        : quizMedia(quiz?.image);
  const quizFinalMessage =
    localized((quiz as any)?.finalMessage, language) ||
    localized((quiz as any)?.final_message, language);
  const renderQuiz = () => {
    const total = quiz?.questions?.length || 0;
    const image = quizImage;
    return (
      <main className={`quiz-screen quiz-screen--${screen}`}>
        <div className="quiz-media">
          {image ? (
            <img
              src={image}
              alt=""
              onError={event => {
                event.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="quiz-media__placeholder" />
          )}
        </div>
        <div className="quiz-content">
          {data.logos && (
            <img src={data.logos} className="museum__partners" alt="" />
          )}
          {renderLanguages()}
          {screen === 'quiz-intro' && (
            <div className="quiz-copy">
              <h1 className="quiz-title">
                {localized(quiz?.title, language) ||
                  localized(quiz?.introTitle, language) ||
                  'Teste os seus conhecimentos'}
              </h1>
              <p className="quiz-description">
                {localized(quiz?.initialMessage, language) ||
                  localized(quiz?.description, language)}
              </p>
            </div>
          )}
          {screen === 'quiz-question' && currentQuestion && (
            <div className="quiz-copy quiz-question-content">
              <div className="quiz-question-header">
                <div className="quiz-question-number">{quizIndex + 1}.</div>
                <h1 className="quiz-question-title">
                  {localized(currentQuestion.prompt, language)}
                </h1>
                <div className="quiz-timer">
                  Tempo Restante
                  <div
                    className="quiz-timer__circle"
                    style={
                      {
                        '--quiz-time-progress': `${quizTimeProgress}%`,
                      } as React.CSSProperties
                    }>
                    {quizRemaining}
                  </div>
                </div>
              </div>
              {quizMedia(currentQuestion.image) && (
                <img
                  className="quiz-question-media"
                  src={quizMedia(currentQuestion.image)}
                  alt=""
                  onError={event => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}
              {quizMedia(currentQuestion.video) && (
                <video
                  className="quiz-question-media"
                  src={quizMedia(currentQuestion.video)}
                  controls
                />
              )}
              {quizMedia(currentQuestion.audio) && (
                <audio
                  className="quiz-question-audio"
                  src={quizMedia(currentQuestion.audio)}
                  controls
                />
              )}
              {currentQuestion.type === 'text' && (
                <input
                  className="quiz-text-answer"
                  value={quizTextAnswer}
                  onChange={event => setQuizTextAnswer(event.target.value)}
                  disabled={quizAnswer !== null}
                  placeholder="Escreva a sua resposta"
                />
              )}
              {currentQuestion.type !== 'text' && (
                <div className="quiz-answers">
                  {currentQuestion.options.map((option: any) => {
                    const selected =
                      quizSelectedIds.map(String).includes(String(option.id)) ||
                      quizFeedback?.selectedIds
                        ?.map(String)
                        .includes(String(option.id));
                    const isCorrectOption = Boolean(
                      quizFeedback?.correctIds
                        ?.map(String)
                        .includes(String(option.id)),
                    );
                    const hasKnownCorrectOptions = Boolean(
                      quizFeedback?.correctIds?.length,
                    );
                    const correct = Boolean(
                      quizFeedback && selected && isCorrectOption,
                    );
                    const incorrect = Boolean(
                      quizFeedback &&
                      selected &&
                      hasKnownCorrectOptions &&
                      !isCorrectOption,
                    );
                    return (
                      <button
                        type="button"
                        key={option.id}
                        disabled={quizAnswer !== null}
                        className={`quiz-answer ${selected ? 'selected' : ''} ${correct ? 'correct' : ''} ${incorrect ? 'incorrect' : ''}`}
                        onClick={() => answerQuiz(option.id)}>
                        {correct && '✓ '}
                        {incorrect && '× '}
                        {localized(option.label, language)}
                      </button>
                    );
                  })}
                </div>
              )}
              {quizFeedback && (
                <div className="quiz-feedback">
                  <h2>
                    {localized(quizFeedback.feedbackTitle, language) ||
                      (quizFeedback.correct ? 'Certo!' : 'Não é bem assim.')}
                  </h2>
                  <p>{localized(quizFeedback.explanation, language)}</p>
                </div>
              )}
            </div>
          )}
          {screen === 'quiz-result' && (
            <div className="quiz-result-copy">
              <h1>A sua pontuação</h1>
              <div className="quiz-score">
                {quizSubmission?.score ?? quizScore} /{' '}
                {Number(quizSubmission?.max_score) > 0
                  ? quizSubmission.max_score
                  : quizMaximumScore}
              </div>
              <div className="quiz-stats">
                {Object.entries(
                  (quizSubmission?.category_scores ||
                    quizCategoryScores) as any,
                ).map(([key, value]: any) => (
                  <div className="quiz-stat" key={key}>
                    {value?.correct !== undefined
                      ? `${value.correct}/${value.answered || value.total || 0}`
                      : value?.score || 0}
                    <br />
                    {value?.name || quizCategoryName(key)}
                  </div>
                ))}
              </div>
              <p className="quiz-description">
                {localized(quizSubmission?.final_feedback, language) ||
                  quizFinalMessage}
              </p>
            </div>
          )}
          <div className="quiz-actions">
            {screen === 'quiz-intro' && (
              <button className="quiz-primary" onClick={startQuiz}>
                Iniciar Quiz
              </button>
            )}
            {screen === 'quiz-question' &&
              quizAnswer === null &&
              (currentQuestion?.type === 'multiple_choice' ||
                currentQuestion?.type === 'text' ||
                quizSelectedIds.length > 1) && (
                <button
                  className="quiz-primary"
                  onClick={submitSelectedQuizAnswer}
                  disabled={
                    currentQuestion?.type === 'text'
                      ? !quizTextAnswer.trim()
                      : !quizSelectedIds.length
                  }>
                  Responder
                </button>
              )}
            {screen === 'quiz-question' && quizAnswer !== null && (
              <button className="quiz-primary" onClick={continueQuiz}>
                Continuar
              </button>
            )}
            {screen === 'quiz-result' && (
              <button className="quiz-primary" onClick={startQuiz}>
                Jogar novamente
              </button>
            )}
            <button
              className="quiz-secondary"
              onClick={() =>
                screen === 'quiz-question'
                  ? setQuizExitConfirm(true)
                  : leaveQuiz()
              }>
              ← Sair
            </button>
          </div>
          {screen === 'quiz-question' && (
            <div className="quiz-progress">
              {quizIndex + 1}/{total}
              <div className="quiz-progress__bar">
                <span
                  style={{
                    width: `${total ? ((quizIndex + 1) / total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {quizExitConfirm && (
          <div className="quiz-exit-backdrop">
            <div className="quiz-exit-dialog">
              <h2>Sair do questionário?</h2>
              <p>{quizAbandonmentMessage}</p>
              <div className="quiz-actions">
                <button
                  className="quiz-primary"
                  onClick={() => setQuizExitConfirm(false)}>
                  Continuar a jogar
                </button>
                <button className="quiz-secondary" onClick={abandonQuiz}>
                  Sim, sair
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  };
  if (quizLoading)
    return (
      <main className="museum kiosk--loading">
        <p>A carregar quiz…</p>
      </main>
    );
  if (quizError && !quiz)
    return (
      <main className="museum kiosk--loading">
        <p>{quizError}</p>
        <button onClick={leaveQuiz}>Voltar</button>
      </main>
    );
  if (
    quiz &&
    (screen === 'quiz-intro' ||
      screen === 'quiz-question' ||
      screen === 'quiz-result')
  )
    return renderQuiz();
  if (screen === 'credits')
    return (
      <main className="museum">
        {renderChrome(
          <div className="credits">
            <h1>
              {language === 'mwl-PT'
                ? 'Créditos i Bibliografia'
                : language === 'en-GB'
                  ? 'Credits and Bibliography'
                  : 'Créditos e Bibliografia'}
            </h1>
            <article>
              <h2>
                {language === 'mwl-PT'
                  ? 'Testos'
                  : language === 'en-GB'
                    ? 'Texts'
                    : 'Textos'}
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: contentHtml(data.credits.texts, language),
                }}
              />
            </article>
            <article>
              <h2>
                {language === 'mwl-PT'
                  ? 'Fotografias'
                  : language === 'en-GB'
                    ? 'Photographs'
                    : 'Fotografias'}
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: contentHtml(data.credits.photography, language),
                }}
              />
            </article>
            <button className="credits__home" onClick={() => setScreen('home')}>
              ⌂{' '}
              {language === 'en-GB'
                ? 'Home'
                : language === 'mwl-PT'
                  ? 'Início'
                  : 'Início'}
            </button>
          </div>,
        )}
      </main>
    );
  if (screen === 'route-detail' && selectedRoute) {
    const routePoints = selectedRoute.points_of_interest ?? [];
    const firstPointImage = contentImage(
      routePoints[0]?.point_of_interest ?? routePoints[0],
    );
    const routeMediaValue = [
      selectedRoute.route_image_media,
      selectedRoute.route_image,
      selectedRoute.file_image_media,
      selectedRoute.image_media,
      selectedRoute.featured_image_media,
      selectedRoute.image,
      selectedRoute.cover_image,
      selectedRoute.cover_media,
    ].find(value => {
      if (!value) return false;
      if (typeof value === 'number') return false;
      if (typeof value === 'string' && /^\d+$/.test(value)) return false;
      return true;
    });
    const routeImage = mediaUrl(routeMediaValue) || firstPointImage;
    const routeQr =
      selectedRoute.qr_code_url ??
      selectedRoute.qr_code ??
      selectedRoute.qrcode_url ??
      selectedRoute.qrcode ??
      selectedRoute.qr;
    const metres = Number(
      selectedRoute.distance ?? selectedRoute.distance_meters ?? 0,
    );
    return (
      <main className="museum museum--route-detail">
        <div className="route-detail-screen">
          <section className="route-detail-hero">
            <img
              src={routeImage}
              alt=""
              onError={event => {
                if (
                  firstPointImage &&
                  event.currentTarget.src !== firstPointImage
                )
                  event.currentTarget.src = firstPointImage;
              }}
            />
            <div className="route-detail-hero__copy">
              <small>
                {language === 'en-US'
                  ? 'ROUTES OF INTEREST'
                  : 'ROTEIROS DE INTERESSE'}
              </small>
              <h1>
                {titleOf(selectedRoute.title ?? selectedRoute.name, language)}
              </h1>
            </div>
          </section>
          <section className="route-detail-stats">
            <div>
              <b>{language === 'en-US' ? 'Duration' : 'Duração'}</b>
              <span>{selectedRoute.duration ?? ''} min</span>
            </div>
            <div>
              <b>{language === 'en-US' ? 'Stops' : 'Paragens'}</b>
              <span>{routePoints.length}</span>
            </div>
            <div>
              <b>{language === 'en-US' ? 'Distance' : 'Distância'}</b>
              <span>{(metres / 1000).toFixed(2)} km</span>
            </div>
          </section>
          <section className="route-detail-body">
            <article className="route-detail-body__text">
              {plainText(
                selectedRoute.description ?? selectedRoute.resume,
                language,
              )}
            </article>
            <aside className="route-detail-qr">
              <img
                src={
                  mediaUrl(routeQr) ||
                  `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(`https://app.mdds.findify.cloud/routes/${selectedRoute.route_id ?? selectedRoute.id ?? ''}`)}`
                }
                alt="QR code"
              />
              <small>
                {language === 'en-US'
                  ? 'Open in mobile app'
                  : 'Abrir na app móvel'}
              </small>
            </aside>
          </section>
          <KioskBottomBar
            banner={data.banner}
            title={data.title}
            languages={renderLanguages()}
            language={language}
            onHome={() => setScreen('home')}
            onBack={() => setScreen('routes')}
          />
        </div>
      </main>
    );
  }
  if (screen === 'routes') {
    const points = (selectedRoute?.points_of_interest ?? [])
      .slice()
      .sort(
        (a: any, b: any) =>
          Number(a.sequence ?? a.position ?? a.order ?? 0) -
          Number(b.sequence ?? b.position ?? b.order ?? 0),
      );
    return (
      <main className="museum museum--routes">
        <div className="routes-layout">
          <header className="routes-heading">
            <small>
              {language === 'en-US'
                ? 'ROUTES OF INTEREST'
                : 'ROTEIROS DE INTERESSE'}
            </small>
            <h1>
              {language === 'en-US'
                ? 'Points of Interest'
                : 'Pontos de Interesse'}
            </h1>
          </header>
          <section
            key={
              selectedRoute.route_id ?? selectedRoute.id ?? selectedRoute.slug
            }
            className="routes-map"
            ref={routeMapRef}
            onClick={() => setSelectedRoutePoint(null)}>
            {selectedRoutePoint && (
              <div
                className={`route-point-tooltip route-point-tooltip--${routeTooltipSide}`}
                style={{
                  left: `${routeTooltipPosition.x}px`,
                  top: `${routeTooltipPosition.y}px`,
                }}
                onPointerDownCapture={async event => {
                  event.stopPropagation();
                  event.preventDefault();
                  const pointId =
                    selectedRoutePoint.point_of_interest_id ??
                    selectedRoutePoint.poi_id ??
                    selectedRoutePoint.id;
                  if (pointId) {
                    const response = await fetch(
                      `${API_URL}/front/${data.domain}/points_of_interest/${encodeURIComponent(pointId)}`,
                    );
                    if (response.ok) {
                      setSelectedItem(await response.json());
                    } else {
                      setSelectedItem(selectedRoutePoint);
                    }
                  } else {
                    setSelectedItem(selectedRoutePoint);
                  }
                  setSelectedRoutePoint(null);
                  setScreen('detail');
                }}>
                <img src={contentImage(selectedRoutePoint)} alt="" />
                <h2>
                  {titleOf(
                    selectedRoutePoint.title ?? selectedRoutePoint.name,
                    language,
                  )}
                </h2>
                <p>{titleOf(selectedRoutePoint.resume, language)}</p>
              </div>
            )}
          </section>
          <aside className="routes-sidebar">
            <h1>{language === 'en-US' ? 'Routes' : 'Roteiros'}</h1>
            <p className="routes-sidebar__subtitle">
              {language === 'en-US'
                ? 'Choose a guided route'
                : 'Escolha um percurso guiado'}
            </p>
            {routes.map((route: any) => (
              <div
                className={`route-option ${
                  (selectedRoute.route_id ??
                    selectedRoute.id ??
                    selectedRoute.slug) ===
                  (route.route_id ?? route.id ?? route.slug)
                    ? 'selected'
                    : ''
                }`}
                key={route.route_id}
                onClick={() => {
                  setSelectedRoutePoint(null);
                  setSelectedRoute(route);
                }}>
                <div>
                  <b>{titleOf(route.title ?? route.name, language)}</b>
                  <span>
                    {route.duration ? `${route.duration} min` : ''}
                    {route.duration && route.points_of_interest?.length
                      ? ' · '
                      : ''}
                    {route.points_of_interest?.length ?? 0}{' '}
                    {language === 'en-US' ? 'stops' : 'paragens'}
                  </span>
                </div>
                <button
                  onClick={event => {
                    event.stopPropagation();
                    setSelectedRoute(route);
                    setScreen('route-detail');
                  }}>
                  →
                </button>
              </div>
            ))}
          </aside>
          <KioskBottomBar
            banner={data.banner}
            title={data.title}
            languages={renderLanguages()}
            language={language}
            onHome={() => setScreen('home')}
          />
        </div>
      </main>
    );
  }
  if (data.workspaceId === 203 && screen === 'detail' && selectedItem && data.marcos?.some((item: any) => String(item.point_of_interest_id) === String(selectedItem.point_of_interest_id))) {
    const index = Math.max(0, data.marcos.findIndex((item: any) => String(item.point_of_interest_id) === String(selectedItem.point_of_interest_id)));
    const readField = (pattern: RegExp, ...keys: string[]) => {
      const direct = keys.map(key => selectedItem?.[key]).find(value => value !== undefined && value !== null && value !== '');
      if (direct !== undefined) return String(titleOf(direct?.value ?? direct?.description ?? direct, language));
      const visit = (value: any, depth: number): string | undefined => {
        if (!value || typeof value !== 'object' || depth > 3) return undefined;
        if (Array.isArray(value)) {
          for (const entry of value) { const found = visit(entry, depth + 1); if (found) return found; }
          return undefined;
        }
        for (const [key, entry] of Object.entries(value)) {
          if (pattern.test(key.replace(/[_-]/g, ' '))) {
            const text = titleOf((entry as any)?.value ?? (entry as any)?.description ?? entry, language);
            if (typeof text === 'string' || typeof text === 'number') return String(text);
          }
          const found = visit(entry, depth + 1); if (found) return found;
        }
        return undefined;
      };
      return visit(selectedItem, 0) || '—';
    };
    const descriptionText = plainText(contentHtml(selectedItem, language), language);
    const emperorFromText = descriptionText.match(/(?:imperador|emperor)\s+([\p{Lu}][\p{L}-]+)/iu)?.[1];
    const dateFromText = descriptionText.match(/(?:erigido|erected)\s+(?:em|in)\s+(\d{3,4}\s*(?:d\.?\s*C\.?|a\.?\s*D\.?))/i)?.[1];
    const distanceFromText = descriptionText.match(/(?:assinala|marks)\s+(?:a\s+)?(milha\s+[IVX\d]+\s+(?:a partir de|desde|from)\s+[^.,;]+)/i)?.[1]?.replace(/a partir de/i, 'desde');
    return (
      <main className="museum museum--marco-detail"><div className="marco-selected">
        <header className="marco-selected__header"><small>MARCOS MILIÁRIOS</small><h1>{titleOf(data.categories.find((item: any) => Number(item.category_id) === 203)?.description, language).split(/<br\s*\/?\s*>|\n/)[0] || 'Os Marcos da Via Romana'}</h1><button onClick={() => { setGlossaryPage(0); setGlossaryOpen(true); }}>{language === 'en-US' ? 'Glossary' : 'Glossário'}</button></header>
        <div className="marco-selected__photo">
          {selectedMarcoImages.length > 0 && <img src={selectedMarcoImages[selectedMarcoImageIndex % selectedMarcoImages.length]} alt={titleOf(selectedItem.title, language)} />}
          {selectedMarcoImages.length > 1 && <>
            <button className="marco-selected__photo-arrow marco-selected__photo-arrow--previous" aria-label={language === 'en-US' ? 'Previous image' : 'Imagem anterior'} onClick={() => setSelectedMarcoImageIndex(index => (index - 1 + selectedMarcoImages.length) % selectedMarcoImages.length)}>‹</button>
            <button className="marco-selected__photo-arrow marco-selected__photo-arrow--next" aria-label={language === 'en-US' ? 'Next image' : 'Imagem seguinte'} onClick={() => setSelectedMarcoImageIndex(index => (index + 1) % selectedMarcoImages.length)}>›</button>
            <div className="marco-selected__photo-dots">{selectedMarcoImages.map((image, imageIndex) => <button key={`${image}-${imageIndex}`} className={imageIndex === selectedMarcoImageIndex ? 'active' : ''} aria-label={`${language === 'en-US' ? 'Image' : 'Imagem'} ${imageIndex + 1}`} onClick={() => setSelectedMarcoImageIndex(imageIndex)} />)}</div>
          </>}
        </div>
        <article className="marco-selected__content"><small>{language === 'en-US' ? `MILESTONE NO. ${titleOf(selectedItem.title, language)}` : `MARCO MILIÁRIO Nº ${titleOf(selectedItem.title, language)}`}</small><h2>{titleOf(selectedItem.name ?? selectedItem.resume ?? selectedItem.title, language)}</h2>
          <div className="marco-selected__meta"><span><b>{language === 'en-US' ? 'EMPEROR' : 'IMPERADOR'}</b>{emperorFromText || readField(/emperor|imperator|ruler|imperador/i, 'emperor', 'imperator', 'ruler', 'imperador')}</span><span><b>{language === 'en-US' ? 'DATE' : 'DATAÇÃO'}</b>{dateFromText || readField(/dating|date|year|datação|data/i, 'dating', 'date', 'year', 'datação')}</span><span><b>{language === 'en-US' ? 'DISTANCE' : 'DISTÂNCIA'}</b>{distanceFromText || readField(/distance|mileage|provenance|location|distância|proveniência/i, 'distance', 'mileage', 'provenance', 'location_description')}</span></div>
          <div className="marco-selected__description" dangerouslySetInnerHTML={{__html: contentHtml(selectedItem, language)}} />
        </article>
        <div className="marcos-initial__stage marco-selected__stage"><div className="marcos-initial__platform" />{data.marcos.map((item: any, i: number) => <button key={item.point_of_interest_id} className={`marcos-initial__pillar ${i === index ? 'active' : ''}`} aria-label={titleOf(item.title, language)} onClick={() => { setSelectedMarcoIndex(i); setSelectedItem(item); }}><b>{titleOf(item.title, language)}</b></button>)}</div>
        <div className="marco-selected__media"><button className="active">{language === 'en-US' ? 'Image' : 'Imagem'}</button>{content3d(selectedItem) && <button onClick={() => setScreen('3d')}>3D</button>}</div>
        <div className="marco-selected__navigation"><button disabled={index <= 0} onClick={() => { setSelectedMarcoIndex(index - 1); setSelectedItem(data.marcos[index - 1]); }}>← {language === 'en-US' ? 'Previous' : 'Anterior'}</button><button disabled={index >= data.marcos.length - 1} onClick={() => { setSelectedMarcoIndex(index + 1); setSelectedItem(data.marcos[index + 1]); }}>{language === 'en-US' ? 'Next' : 'Seguinte'} →</button></div>
        <button className="marco-selected__home" onClick={() => { setSelectedMenu({label: {'pt-PT': 'Marcos'}, __marcos: true}); setScreen('category'); }}>⌂ {language === 'en-US' ? 'Main menu' : 'menu principal'}</button>
        <KioskBottomBar banner={data.banner} title={data.title} languages={renderLanguages()} language={language} onHome={() => { setSelectedMenu({label: {'pt-PT': 'Marcos'}, __marcos: true}); setScreen('category'); }} showActions={false} />
        {renderGlossaryModal()}
      </div></main>
    );
  }
  if (screen === 'detail' && selectedItem) {
    const category = selectedMenu ? categoryFor(selectedMenu) : undefined;
    if (isCollection(selectedMenu)) {
      const categoryByParent = (slug: string) => {
        const parent = data.categories.find(
          (item: any) =>
            String(item.page_slug ?? item.slug ?? '').toLowerCase() === slug,
        );
        return data.categories.filter(
          (item: any) =>
            String(
              item.parent_id ??
                item.parent_category_id ??
                item.parent?.category_id,
            ) === String(parent?.category_id) &&
            categoryIdsOf(selectedItem).includes(Number(item.category_id)),
        );
      };
      const detailCategories = [
        ...categoryByParent('period'),
        ...categoryByParent('tipologia'),
      ];
      return (
        <main className="museum museum--collection-detail">
          <div className="collection-detail">
            <img
              className="collection-detail__image"
              src={contentImage(selectedItem)}
              alt=""
            />
            <section className="collection-detail__content">
              <small>
                {language === 'en-US'
                  ? 'COLLECTION · HERITAGE'
                  : 'COLEÇÃO · ESPÓLIO'}
              </small>
              <h1>{titleOf(selectedItem.name, language)}</h1>
              <div className="collection-detail__categories">
                {detailCategories.map((category: any) => (
                  <span key={category.category_id}>
                    {titleOf(category.name, language)}
                  </span>
                ))}
              </div>
              {[
                ['Material', 'capacidade'],
                [language === 'en-US' ? 'Dating' : 'Datação', 'horário'],
                [
                  language === 'en-US' ? 'Provenance' : 'Proveniência',
                  'endereço',
                ],
                [
                  language === 'en-US' ? 'Inventory no.' : 'Nº inventário',
                  'notas',
                ],
              ].map(([label, key]) => (
                <div className="collection-detail__field" key={key}>
                  <b>{label}</b>
                  <span>{collectionField(selectedItem, key)}</span>
                </div>
              ))}
              <h2>
                {language === 'en-US' ? 'ABOUT THE OBJECT' : 'SOBRE A PEÇA'}
              </h2>
              <article
                dangerouslySetInnerHTML={{
                  __html: contentHtml(selectedItem, language),
                }}
              />
            </section>
            <KioskBottomBar
              banner={data.banner}
              title={data.title}
              languages={renderLanguages()}
              language={language}
              onHome={() => setScreen('home')}
              onBack={() => {
                setSelectedItem(null);
                setScreen('category');
              }}
            />
          </div>
        </main>
      );
    }
    return (
      <main className="museum museum--detail">
        <div className="detail">
          <section className="detail__hero">
            <img
              src={
                contentImage(selectedItem) ||
                mediaUrl(selectedItem.featured_image_media)
              }
              alt=""
            />
            <div className="detail__hero-copy">
              <small>
                {titleOf(selectedItem.title ?? selectedItem.name, language)}
              </small>
              <h1>
                {titleOf(category?.description, language) ||
                  titleOf(selectedItem.resume, language)}
              </h1>
            </div>
          </section>
          <section className="detail__body">
            <span>
              {language === 'en-US'
                ? 'ABOUT THE EXHIBITION'
                : 'SOBRE A EXPOSIÇÃO'}
            </span>
            <div className="detail__scroll-wrap">
              <article
                dangerouslySetInnerHTML={{
                  __html: contentHtml(selectedItem, language),
                }}
              />
              <aside>
                <b>⌄</b>{' '}
                {language === 'en-US'
                  ? 'swipe for more'
                  : 'deslize para ler mais'}
                <i />
              </aside>
            </div>
          </section>
          <KioskBottomBar
            banner={data.banner}
            title={data.title}
            languages={renderLanguages()}
            language={language}
            onHome={() => setScreen('home')}
            onBack={
              selectedRoute ||
              /arqueologia e o museu/i.test(
                titleOf(selectedMenu?.label, language),
              )
                ? () => {
                    setSelectedItem(null);
                    setScreen(selectedRoute ? 'routes' : 'category');
                  }
                : undefined
            }
          />
          {selectedItem && (
            <div
              className="detail-modal"
              role="dialog"
              onClick={() => setSelectedItem(null)}>
              <div
                className="detail-modal__panel"
                onClick={event => event.stopPropagation()}>
                <img src={contentImage(selectedItem)} alt="" />
                <h2>{titleOf(selectedItem.name, language)}</h2>
                <p>
                  {categoryIdsOf(selectedItem)
                    .map((id: number) =>
                      titleOf(
                        data.categories.find(
                          (category: any) =>
                            Number(category.category_id) === id,
                        )?.name,
                        language,
                      ),
                    )
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {[
                  [
                    language === 'en-US' ? 'Material' : 'Material',
                    'capacidade',
                  ],
                  [language === 'en-US' ? 'Dating' : 'Datação', 'horário'],
                  [
                    language === 'en-US' ? 'Provenance' : 'Proveniência',
                    'endereço',
                  ],
                  [
                    language === 'en-US' ? 'Inventory no.' : 'Nº inventário',
                    'notas',
                  ],
                ].map(([label, key]) => (
                  <div key={key}>
                    <b>{label}: </b>
                    {collectionField(selectedItem, key)}
                  </div>
                ))}
                <div
                  dangerouslySetInnerHTML={{
                    __html: contentHtml(selectedItem, language),
                  }}
                />
                <div className="detail-modal__actions">
                  <button onClick={() => setScreen('home')}>
                    ⌂ {language === 'en-US' ? 'Main menu' : 'Menu principal'}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setScreen(selectedRoute ? 'routes' : 'detail');
                    }}>
                    ← {language === 'en-US' ? 'Back' : 'Voltar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    );
  }
  const renderCollectionCategory = () => {
    const root = data.categories.find((category: any) =>
      /colection|collection/i.test(
        String(category.page_slug ?? category.slug ?? ''),
      ),
    );
    const children = (parentSlug: string) => {
      const parent = data.categories.find(
        (category: any) =>
          String(category.page_slug ?? category.slug ?? '').toLowerCase() ===
          parentSlug,
      );
      return data.categories.filter(
        (category: any) =>
          String(
            category.parent_id ??
              category.parent_category_id ??
              category.parent?.category_id,
          ) === String(parent?.category_id),
      );
    };
    const periodOptions = children('period');
    const typeOptions = children('tipologia');
    const selectedItems = items.filter(
      (item: any) =>
        (!collectionPeriod ||
          categoryIdsOf(item).includes(Number(collectionPeriod))) &&
        (!collectionTypology ||
          categoryIdsOf(item).includes(Number(collectionTypology))),
    );
    const isChronology = /arqueologia e o museu/i.test(
      titleOf(selectedMenu?.label, language),
    );
    if (isChronology) {
      return (
        <main className="museum">
          <div className="chronology">
            <header className="chronology-heading">
              <small>
                {language === 'en-US' ? 'CHRONOLOGY' : 'CRONOLOGIA'}
              </small>
              <h1>
                {language === 'en-US'
                  ? 'A History of the Museum'
                  : 'A História do Museu'}
              </h1>
              <p>
                {language === 'en-US'
                  ? '← slide horizontally to explore the eras →'
                  : '← deslize horizontalmente para explorar as épocas →'}
              </p>
            </header>
            <div className="chronology-grid">
              {items.map((item: any) => (
                <button
                  className="chronology-card"
                  key={item.content_id ?? item.id}
                  onClick={() => {
                    setSelectedItem(item);
                    setScreen('detail');
                  }}>
                  <img src={contentImage(item)} alt="" />
                  <strong>{titleOf(item.name ?? item.title, language)}</strong>
                  <small>{plainText(item.resume, language)}</small>
                  <p>{firstParagraph(item, language)}</p>
                </button>
              ))}
            </div>
            <div className="chronology-scrollbar">
              <span />
            </div>
            <KioskBottomBar
              banner={data.banner}
              title={data.title}
              languages={renderLanguages()}
              language={language}
              onHome={() => setScreen('home')}
            />
          </div>
        </main>
      );
    }
    const chipGroup = (
      label: string,
      options: any[],
      value: string,
      setter: Function,
    ) => (
      <div className="collection-filter-group">
        <b>{label}</b>
        <div>
          {[
            {
              category_id: '',
              name: {
                'pt-PT': label === 'PERÍODO' ? 'Todos' : 'Todas',
                'en-US': 'All',
              },
            } as any,
            ...options,
          ].map((option: any) => (
            <button
              className={
                String(value) === String(option.category_id) ? 'selected' : ''
              }
              key={`${label}-${option.category_id}`}
              onClick={() => setter(String(option.category_id))}>
              {titleOf(option.name, language)}
            </button>
          ))}
        </div>
      </div>
    );
    return (
      <main className="museum">
        <div className="category category--collection">
          <header className="collection-heading">
            <small>
              {language === 'en-US'
                ? 'COLLECTION · HERITAGE'
                : 'COLEÇÃO · ESPÓLIO'}
            </small>
            <h1>
              {language === 'en-US' ? 'Museum Collection' : 'Coleção do Museu'}
            </h1>
            <p>
              {language === 'en-US'
                ? `${selectedItems.length} objects in the collection — filter by period or typology`
                : `${selectedItems.length} peças no acervo — filtre por período ou tipologia`}
            </p>
          </header>
          <div
            className="collection-grid"
            onWheel={event => {
              if (Math.abs(event.deltaY) > Math.abs(event.deltaX))
                event.currentTarget.scrollLeft += event.deltaY;
            }}
            onScroll={event => {
              const element = event.currentTarget;
              const max = element.scrollWidth - element.clientWidth;
              const progress = max ? (element.scrollLeft / max) * 400 : 0;
              element.parentElement
                ?.querySelector<HTMLElement>('.collection-scrollbar span')
                ?.style.setProperty('transform', `translateX(${progress}%)`);
            }}>
            {selectedItems.map((item: any) => (
              <button
                className="collection-card"
                key={item.point_of_interest_id ?? item.content_id}
                onClick={() => {
                  setSelectedItem(item);
                  setScreen('detail');
                }}>
                <img src={contentImage(item)} alt="" />
                <strong>{titleOf(item.title ?? item.name, language)}</strong>
                <span>
                  {[...typeOptions, ...periodOptions]
                    .filter((category: any) =>
                      categoryIdsOf(item).includes(
                        Number(category.category_id),
                      ),
                    )
                    .map((category: any) => titleOf(category.name, language))
                    .join(' · ')}
                </span>
              </button>
            ))}
          </div>
          <div className="collection-scrollbar">
            <span />
          </div>
          <div className="collection-filters">
            {chipGroup(
              language === 'en-US' ? 'PERIOD' : 'PERÍODO',
              periodOptions,
              collectionPeriod,
              setCollectionPeriod,
            )}
            {chipGroup(
              language === 'en-US' ? 'TYPOLOGY' : 'TIPOLOGIA',
              typeOptions,
              collectionTypology,
              setCollectionTypology,
            )}
          </div>
          <KioskBottomBar
            banner={data.banner}
            title={data.title}
            languages={renderLanguages()}
            language={language}
            onHome={() => setScreen('home')}
          />
          {selectedItem && (
            <div className="detail-modal" onClick={() => setSelectedItem(null)}>
              <div
                className="detail-modal__panel"
                onClick={event => event.stopPropagation()}>
                <img src={contentImage(selectedItem)} alt="" />
                <h2>{titleOf(selectedItem.name, language)}</h2>
                <p>
                  {categoryIdsOf(selectedItem)
                    .map((id: number) =>
                      titleOf(
                        data.categories.find(
                          (category: any) =>
                            Number(category.category_id) === id,
                        )?.name,
                        language,
                      ),
                    )
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: contentHtml(selectedItem, language),
                  }}
                />
                <div className="detail-modal__actions">
                  <button onClick={() => setScreen('home')}>
                    ⌂ {language === 'en-US' ? 'Main menu' : 'Menu principal'}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setScreen(
                        selectedRoute ||
                          /arqueologia e o museu/i.test(
                            titleOf(selectedMenu?.label, language),
                          )
                          ? selectedRoute
                            ? 'routes'
                            : 'category'
                          : 'detail',
                      );
                    }}>
                    ← {language === 'en-US' ? 'Back' : 'Voltar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    );
  };
  if (screen === 'category' && isCollection(selectedMenu))
    return renderCollectionCategory();
  const renderMarcosDefault = () => {
    const marcos = data.marcos ?? [];
    const marcosCategory = data.categories.find((item: any) => Number(item.category_id) === 203);
    const marcosDescriptionLines = plainText(marcosCategory?.description, language)
      .split(/\n+/)
      .map((line: string) => line.trim())
      .filter(Boolean);
    const categoryTitle = marcosDescriptionLines[0] || 'Os Marcos da Via Romana';
    const selectionLabel = marcosDescriptionLines[1] || 'Selecione um Marco Miliário';
    const selectMarco = (index: number) => { setSelectedMarcoIndex(index); setSelectedItem(marcos[index]); setSelectedMenu({label: {'pt-PT': 'Marcos'}, __marcos: true}); setScreen('detail'); };
    return <main className="museum museum--marcos"><div className="marcos-default">
      <header className="marcos-default__header"><small>MARCOS MILIÁRIOS</small><h1>{categoryTitle}</h1></header>
      <button className="marcos-default__glossary" onClick={() => { setGlossaryPage(0); setGlossaryOpen(true); }}>{language === 'en-US' ? 'Glossary' : 'Glossário'}</button>
      <div className="marcos-initial__stage"><div className="marcos-initial__platform" />{marcos.map((item: any, index: number) => <button key={item.point_of_interest_id} className="marcos-initial__pillar" onClick={() => selectMarco(index)}><b>{titleOf(item.title, language)}</b><img src={contentImage(item)} alt={`Marco ${titleOf(item.title, language)}`} /></button>)}</div>
      <h3>{selectionLabel}</h3><KioskBottomBar banner={data.banner} title={data.title} languages={renderLanguages()} language={language} onHome={() => setStarted(false)} showActions={false} />{renderGlossaryModal()}</div></main>;
  };
  if (data.workspaceId === 203 && screen === 'category' && selectedMenu?.__marcos) {
    return renderMarcosDefault();
  }
  if (data.workspaceId === 203 && screen === 'category' && selectedMenu?.__glossary) {
    return (
      <main className="museum museum--glossary"><div className="glossary-screen"><header><small>INFORMAÇÃO</small><h1>Glossário</h1></header><div className="glossary-grid">{(items.length ? items : data.glossary ?? []).map((item: any) => <button key={item.content_id} onClick={() => { setSelectedItem(item); setScreen('detail'); }}><strong>{titleOf(item.title ?? item.name, language)}</strong><span>{firstParagraph(item, language)}</span></button>)}</div><KioskBottomBar banner={data.banner} title={data.title} languages={renderLanguages()} language={language} onHome={() => setScreen('home')} /></div></main>
    );
  }
  if (data.workspaceId === 203 && screen === '3d' && selectedItem && content3d(selectedItem)) {
    const zoomModel = (factor: number) => {
      const viewer = modelViewerRef.current;
      const orbit = viewer?.getCameraOrbit?.();
      if (!viewer || !orbit) return;
      const radius = Math.max(0.2, Math.min(orbit.radius * factor, orbit.radius * 2));
      viewer.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${radius}m`;
    };
    return <main className="museum museum--3d"><section className="model-screen">
      <header className="model-screen__heading"><small>{language === 'en-US' ? `MILESTONE NO. ${titleOf(selectedItem.title, language)} · 3D MODEL` : `MARCO MILIÁRIO Nº ${titleOf(selectedItem.title, language)} · MODELO 3D`}</small><h1>{titleOf(selectedItem.name ?? selectedItem.resume ?? selectedItem.title, language)}</h1></header>
      {React.createElement('model-viewer' as any, {
        ref: modelViewerRef,
        src: content3d(selectedItem),
        alt: titleOf(selectedItem.name ?? selectedItem.title, language),
        'camera-controls': true,
        'camera-orbit': '0deg 75deg 320%',
        'interaction-prompt': 'none',
        'environment-image': 'neutral',
        exposure: '1',
        'shadow-intensity': '0.8',
        'touch-action': 'pan-y',
        ar: false,
        reveal: 'auto',
        className: 'model-screen__viewer',
      })}
      <button className="model-screen__close" aria-label={language === 'en-US' ? 'Close 3D view' : 'Fechar visualização 3D'} onClick={() => setScreen('detail')}>×</button>
      <div className="model-screen__zoom"><button aria-label={language === 'en-US' ? 'Zoom in' : 'Aumentar zoom'} onClick={() => zoomModel(.82)}>+</button><button aria-label={language === 'en-US' ? 'Zoom out' : 'Reduzir zoom'} onClick={() => zoomModel(1.22)}>−</button></div>
      <p className="model-screen__hint">{language === 'en-US' ? 'Drag to rotate · Pinch to zoom' : 'Arraste para rodar · Afaste os dedos para ampliar'}</p>
    </section></main>;
  }
  if (screen === 'category') {
    const category = categoryFor(selectedMenu);
    const collection = isCollection(selectedMenu);
    const directoryRoot = data.categories.find((item: any) =>
      /^(pisos|floors)$/i.test(
        String(item.page_slug ?? item.slug ?? '') ||
          titleOf(item.name, language),
      ),
    );
    const directoryFloors = directoryRoot
      ? data.categories
          .filter(
            (item: any) =>
              String(item.parent_category_id ?? item.parent_id) ===
              String(directoryRoot.category_id),
          )
          .sort(
            (a: any, b: any) =>
              (Date.parse(
                b.publish_date ?? b.public_date ?? b.make_public_on ?? '',
              ) || 0) -
              (Date.parse(
                a.publish_date ?? a.public_date ?? a.make_public_on ?? '',
              ) || 0),
          )
      : [];
    const isDirectory =
      Boolean(directoryRoot) &&
      (/diret[oó]rio do edif[ií]cio/i.test(
        titleOf(selectedMenu?.label, language),
      ) ||
        String(category?.page_slug ?? category?.slug ?? '').toLowerCase() ===
          'floors');
    if (isDirectory && directoryFloors.length) {
      const floor =
        directoryFloors.find(
          (item: any) => String(item.category_id) === String(selectedFloorId),
        ) ?? directoryFloors[0];
      const legend = plainText(floor.description, language)
        .split(/\n+/)
        .map(item => item.trim())
        .filter(Boolean);
      return (
        <main className="museum museum--directory">
          <div className="directory-screen">
            <header className="directory-heading">
              <small>
                {language === 'en-US'
                  ? 'BUILDING DIRECTORY'
                  : 'DIRETÓRIO DO EDIFÍCIO'}
              </small>
              <h1>
                {language === 'en-US'
                  ? 'Floors and Services'
                  : 'Pisos e Serviços'}
              </h1>
            </header>
            <div className="directory-plan">
              <img
                src={contentImage(floor)}
                alt={titleOf(floor.name, language)}
              />
            </div>
            <div className="directory-legend">
              {legend.map((label, index) => (
                <div key={`${label}-${index}`}>
                  <span>{label}</span>
                  <b>{index + 1}</b>
                </div>
              ))}
            </div>
            <div className="directory-floor-switch">
              {directoryFloors.map((item: any) => (
                <button
                  key={item.category_id}
                  className={
                    String(item.category_id) === String(floor.category_id)
                      ? 'active'
                      : ''
                  }
                  onClick={() => setSelectedFloorId(String(item.category_id))}>
                  {titleOf(item.name, language)}
                </button>
              ))}
            </div>
            <KioskBottomBar
              banner={data.banner}
              title={data.title}
              languages={renderLanguages()}
              language={language}
              onHome={() => setScreen('home')}
            />
          </div>
        </main>
      );
    }
    const periods = collection ? collectionChildren('period') : [];
    const typologies = collection ? collectionChildren('tipologia') : [];
    const visibleItems = collection
      ? items.filter(
          (item: any) =>
            (!collectionPeriod ||
              categoryIdsOf(item).includes(Number(collectionPeriod))) &&
            (!collectionTypology ||
              categoryIdsOf(item).includes(Number(collectionTypology))),
        )
      : items;
    if (
      !collection &&
      /arqueologia e o museu/i.test(titleOf(selectedMenu?.label, language))
    ) {
      const chronologyItems = visibleItems.slice().sort((a: any, b: any) => {
        const publishedAt = (item: any) =>
          Date.parse(item.publish_date ?? item.public_date ?? '') || 0;
        return publishedAt(b) - publishedAt(a);
      });
      return (
        <main className="museum">
          <div className="chronology">
            <header className="chronology-heading">
              <small>
                {language === 'en-US' ? 'CHRONOLOGY' : 'CRONOLOGIA'}
              </small>
              <h1>
                {language === 'en-US'
                  ? 'A History of the Museum'
                  : 'A História do Museu'}
              </h1>
              <p>
                {language === 'en-US'
                  ? '← slide horizontally to explore the eras →'
                  : '← deslize horizontalmente para explorar as épocas →'}
              </p>
            </header>
            <div className="chronology-grid">
              {chronologyItems.map((item: any) => (
                <button
                  className="chronology-card"
                  key={item.content_id ?? item.id}
                  onClick={() => {
                    setSelectedItem(item);
                    setScreen('detail');
                  }}>
                  <img src={contentImage(item)} alt="" />
                  <strong>{titleOf(item.name ?? item.title, language)}</strong>
                  <small>{plainText(item.resume, language)}</small>
                  <p>{firstParagraph(item, language)}</p>
                </button>
              ))}
            </div>
            <div className="chronology-scrollbar">
              <span />
            </div>
            <KioskBottomBar
              banner={data.banner}
              title={data.title}
              languages={renderLanguages()}
              language={language}
              onHome={() => setScreen('home')}
            />
          </div>
        </main>
      );
    }
    const visualItems = visibleItems
      .reduce<any[]>((ordered, item, index) => {
        const page = Math.floor(index / 12);
        const position = index % 12;
        const column = position < 6 ? position : position - 6;
        const row = position < 6 ? 0 : 1;
        ordered[page * 12 + column * 2 + row] = item;
        return ordered;
      }, [])
      .filter(Boolean);
    return (
      <main className="museum">
        {renderChrome(
          <div className="category">
            <p className="category__description">
              {titleOf(category?.description, language)}
            </p>
            <div
              className="poi-grid"
              onWheel={event => {
                if (Math.abs(event.deltaY) > Math.abs(event.deltaX))
                  event.currentTarget.scrollLeft += event.deltaY;
              }}
              onScroll={event => {
                const element = event.currentTarget;
                const max = element.scrollWidth - element.clientWidth;
                const progress = max ? (element.scrollLeft / max) * 400 : 0;
                element.parentElement
                  ?.querySelector<HTMLElement>('.category__scrollbar span')
                  ?.style.setProperty('transform', `translateX(${progress}%)`);
              }}>
              {visualItems.map((item, index) => {
                const position = index % 12;
                const page = Math.floor(index / 12);
                const column = Math.floor(position / 2);
                const row = position % 2;
                const bottomOffsets = [2.37, 1.7, 2.37, 2.37, 2.37, 2.37];
                const left = `calc(${page * 98.1 + column * 16.35 + (row ? bottomOffsets[column] : 0)}vw)`;
                const top = `${(row ? 35.2 : 0) + (row ? [-5, -2, -4, -2.5, -4, -2][column] : [0, 2.2, 0, 2.8, 0.7, 3.5][column])}vh`;
                const image = collection
                  ? contentImage(item)
                  : mediaUrl(item.featured_image_media);
                return (
                  <button
                    className="poi-card"
                    style={{left, top}}
                    key={item.point_of_interest_id ?? item.content_id}
                    onClick={() => setSelectedItem(item)}>
                    <img src={image} alt="" />
                    <span>
                      {titleOf(collection ? item.name : item.title, language)}
                    </span>
                    <small>{titleOf(item.resume, language)}</small>
                  </button>
                );
              })}
            </div>
            <div className="category__scrollbar">
              <span />
            </div>
            <button
              className="category__home"
              onClick={() => setScreen('home')}>
              ⌂ {language === 'en-US' ? 'Main menu' : 'Menu principal'}
            </button>
            {selectedItem && (
              <div
                className="detail-modal"
                role="dialog"
                onClick={() => setSelectedItem(null)}>
                <div
                  className="detail-modal__panel"
                  onClick={event => event.stopPropagation()}>
                  <img
                    src={
                      collection
                        ? contentImage(selectedItem)
                        : mediaUrl(selectedItem.featured_image_media)
                    }
                    alt=""
                  />
                  <h2>
                    {titleOf(
                      collection ? selectedItem.name : selectedItem.title,
                      language,
                    )}
                  </h2>
                  {collection ? (
                    <>
                      <p>
                        {categoryIdsOf(selectedItem)
                          .map((id: number) =>
                            titleOf(
                              data.categories.find(
                                (c: any) => Number(c.category_id) === id,
                              )?.name,
                              language,
                            ),
                          )
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      {[
                        [
                          language === 'en-US' ? 'Material' : 'Material',
                          'capacidade',
                        ],
                        [
                          language === 'en-US' ? 'Dating' : 'Datação',
                          'horário',
                        ],
                        [
                          language === 'en-US' ? 'Provenance' : 'Proveniência',
                          'endereço',
                        ],
                        [
                          language === 'en-US'
                            ? 'Inventory no.'
                            : 'Nº inventário',
                          'notas',
                        ],
                      ].map(([label, key]) => (
                        <div key={key}>
                          <b>{label}: </b>
                          {collectionField(selectedItem, key)}
                        </div>
                      ))}
                    </>
                  ) : (
                    <p>{titleOf(selectedItem.resume, language)}</p>
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: contentHtml(selectedItem, language),
                    }}
                  />
                  <div className="detail-modal__actions">
                    <button onClick={() => setScreen('home')}>
                      ⌂ {language === 'en-US' ? 'Main menu' : 'Menu principal'}
                    </button>
                    <button onClick={() => setSelectedItem(null)}>
                      ← {language === 'en-US' ? 'Back' : 'Voltar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>,
          menuLabel(selectedMenu),
          false,
        )}
      </main>
    );
  }
  if (data.workspaceId === 203) return renderMarcosDefault();
  return (
    <main className="museum museum--home">
      {renderChrome(
        <div className="home">
          <div className="menu-grid">
            {data.menu.map((menu: any) => {
              const category = categoryFor(menu);
              const logo = menuLogo(menu);
              return (
                <button
                  className="menu-card"
                  key={menu.id}
                  onClick={() => {
                    const slug = quizSlugFromMenu(menu);
                    if (slug) openQuiz(menu, slug);
                    else if (menu.content_id) void openContent(menu);
                    else void openCategory(menu);
                  }}>
                  <img src={mediaUrl(category?.featured_image_media)} alt="" />
                  <span className="menu-card__icon">
                    {logo && <img src={logo} alt="" />}
                  </span>
                  <small>{titleOf(category?.description, language)}</small>
                  <strong>{menuLabel(menu)}</strong>
                </button>
              );
            })}
          </div>
          <KioskBottomBar
            banner={data.banner}
            title={data.title}
            languages={renderLanguages()}
            language={language}
            onHome={() => setScreen('home')}
            showActions={false}
            showLanguages={false}
          />
        </div>,
      )}
    </main>
  );
};
